import type { RawCard } from '../api/cards';

export type CardKind = 'physical' | 'magic' | 'enemy';

export type AtomicEffect =
  | { type: 'damage'; amount: number }
  // Deal damage directly to HP, bypassing Armor (does not reduce Armor).
  | { type: 'damageIgnoreArmor'; amount: number }
  // Deal damage equal to the source fighter's current armor.
  | { type: 'damageEqualToArmor' }
  | { type: 'damageEqualToReflect' }
  | { type: 'block'; amount: number }
  // Directly set armor to an absolute value.
  | { type: 'setArmor'; value: number }
  // Directly set the opponent's armor to an absolute value.
  // Used by effects like: "Destroy all enemy Armor." (i.e. set to 0).
  | { type: 'setOpponentArmor'; value: number }
  | { type: 'convertArmorToSp' }
  | { type: 'convertReflectToHp' }
  // The next card you play costs 0 (HP + SP). Consumed on the next successful play.
  | { type: 'nextCardCost0' }
  // Enemy debuff: the player's next Magic card costs double SP. Consumed on the next successful Magic play.
  | { type: 'nextMagicCardCostDoubleSP' }
  // Enemy debuff: the player's card effects are weakened for N player turns.
  | { type: 'weakenPlayerCards'; turns: number }
  // Player-applied debuff: enemy deals reduced damage for N enemy turns.
  // Example: \"Enemy deals 50% less damage for the next 2 turns.\" => mult = 0.5, turns = 2.
  | { type: 'weakenEnemyDamage'; mult: number; turns: number }
  // Double the damage you deal for the rest of this player turn (stacks multiplicatively).
  | { type: 'doubleDamageDealtThisTurn' }
  // Double the damage you deal on your NEXT player turn (stacks multiplicatively).
  | { type: 'doubleDamageDealtNextTurn' }
  // Double the damage you take on the next enemy turn (stacks multiplicatively).
  | { type: 'doubleDamageTakenNextTurn' }
  | { type: 'setHP'; value: number }
  | { type: 'setSP'; value: number }
  | { type: 'die' }
  | { type: 'doubleArmor' }
  | { type: 'doubleHP' }
  | { type: 'doubleSP' }
  // Variable trade effects that require choosing X at play time.
  // Example: "Lose X Health Points. Gain X Sanity Points."
  | { type: 'xTrade'; from: 'hp' | 'sp'; to: 'hp' | 'sp' }
  | { type: 'frenzy' }
  | { type: 'tutor'; kind: 'physical' | 'magic' | 'any'; zone?: 'deck' | 'discard' }
  // Gain Armor equal to `amountPer` times the number of cards the player has played this turn.
  // Used by Addendum effects like: "Gain 10 Armor for each card you played this turn."
  | { type: 'blockPerPlaysThisTurn'; amountPer: number }
  | { type: 'healHPPerPlaysThisTurn'; amountPer: number }
  | { type: 'healSPPerPlaysThisTurn'; amountPer: number }
  | { type: 'healHP'; amount: number }
  | { type: 'healSP'; amount: number }
  // Temporarily increases the player's maximum HP cap for the duration of this combat.
  // (Also raises Armor/Reflect caps, which are tied to Max HP.)
  | { type: 'gainMaxHpThisCombat'; amount: number }
  // This turn, gain HP for each point of damage you deal (a lifesteal-like buff).
  // Example: "This turn, gain 1 HP for every point of damage you deal." (Grim Renewal)
  | { type: 'healOnDamageDealtThisTurn'; amountPerDamage: number }
  | { type: 'reduceUtilityCardCostsThisTurn'; amount: number }
  | { type: 'loseHP'; amount: number }
  | { type: 'loseSP'; amount: number }
  | { type: 'nextAttackBonus'; amount: number }
  // The next Attack-tagged card played ignores enemy Armor.
  // Count-based so multiple triggers can stack.
  | { type: 'nextAttackIgnoreArmor'; count?: number }
  // Reduce all pending Delay bundles by N turns (can trigger immediately).
  | { type: 'reduceAllDelay'; amount: number }
  | { type: 'draw'; amount: number; sourceKind?: 'physical' | 'magic' | 'any' }
  // Draw extra cards randomly from piles.
  | { type: 'drawRandom'; amount: number }
  | { type: 'drawRandomPhysical'; amount: number }
  | { type: 'drawRandomMagic'; amount: number }
  // Draw cards from a specific deck (used for choice-based draws like Premonition).
  // When setCostTo0 is true, the drawn card's instance cost is set to 0.
  | { type: 'drawFromDeck'; kind: 'physical' | 'magic' | 'any'; amount: number; setCostTo0?: boolean }
  | { type: 'peekReorderPhysical'; count: number }
  | { type: 'peekReorderMagic'; count: number }
  | { type: 'peekReorderDeck'; count: number }
  | { type: 'peekPickOneDiscardRestPhysical'; count: number }
  | { type: 'peekPickOneDiscardRestMagic'; count: number }
  | { type: 'peekPickOneDiscardRestAny'; count: number }
  | { type: 'reflect'; percent: number }
  | { type: 'enableReflectOverflowBurst' }
  // Next time you reflect damage back to the enemy, gain SP equal to the reflected damage.
  // Consumed on the next reflect trigger.
  | { type: 'nextReflectGainSp' }
  // Next time you reflect damage, trigger the reflect event twice.
  // Consumed on the next reflect trigger.
  | { type: 'nextReflectTriggerTwice' }
  | { type: 'stun'; turns: number }
  | { type: 'discard'; amount: number }
  | { type: 'enableDiscardPlayThisTurn' }
  | { type: 'negateNextDamage' }
  // Resolve all scheduled Delay bundles immediately, but at half strength.
  // Used by cards like "Hasten the Inevitable".
  | { type: 'resolveAllDelayHalved' };

export type ChooseOneOption = {
  label: string;
  effects: AtomicEffect[];
  // Optional delayed effects that trigger after N player turns.
  // Used for "Choose one" options that include clauses like "... next turn".
  delayed?: { turns: number; effects: Effect[] }[];
};

export type ConditionalEffect = {
  // If the opponent (enemy when played by the player, or player when played by the enemy) is currently stunned,
  // apply `then` effects; otherwise apply `else` effects.
  // Used by cards like Backstab.
  type: 'ifOpponentHasStun';
  then: AtomicEffect[];
  else: AtomicEffect[];
};

export type Effect = AtomicEffect | ConditionalEffect | { type: 'chooseOne'; options: ChooseOneOption[] };



export type CombatUiStatusPulseKey =
  | 'reflect'
  | 'negate'
  | 'atkBonus'
  | 'free'
  | 'dmgUp'
  | 'dmgDown'
  | 'magicDouble'
  | 'weaken'
  | 'enemyStun'
  | 'enemyWeaken';

export type RelicFeedbackTriggerKey =
  | 'startArmor'
  | 'startHp'
  | 'startSp'
  | 'startReflectPct'
  | 'startNegateNextDamage'
  | 'startNextCardCost0'
  | 'turnStartAttackBonus'
  | 'turnStartArmor'
  | 'firstTurnExtraDraw'
  | 'drawBeyondFirstGainSp'
  | 'fullHandGainHpOnDraw'
  | 'onDiscardGainSp'
  | 'onDiscardGainHp'
  | 'delayCardArmor'
  | 'spGainWhenZero'
  | 'reflectBonusHp'
  | 'reflectBonusSp'
  | 'reflectBonusDraw'
  | 'attackBonusFlat'
  | 'attackBonusWhenLowSp'
  | 'attackBonusWhenArmorZero'
  | 'endTurnEmptyDraw'
  | 'endTurnFiveCardsArmor'
  | 'endTurnArmorPerCard'
  | 'chainCostReduction'
  | 'delayCostReduction'
  | 'addendumCostReduction'
  | 'damageReductionIfLargeDeck'
  | 'reduceDelayForLargeDeck'
  | 'doubleArmorGainFromCards'
  | 'firstExileDontExile'
  | 'immuneStun'
  | 'focusBonusSP'
  | 'focusBonusHP'
  | 'recastDrawPenalty';

export type RelicFeedbackEntry = {
  relicId: string;
  amount?: number;
  text?: string;
};

export type CombatUiEvent =
  | { id: string; kind: 'relicTriggered'; relicId: string; text?: string }
  | { id: string; kind: 'statusPulse'; key: CombatUiStatusPulseKey };

export type Addendum = {
  // Addendum costs can be expressed in SP or HP.
  // Exactly one of these should be set.
  costSp?: number | 'X';
  costHp?: number | 'X';
  // Text after the Addendum marker (e.g. "Deal 35 damage instead. Chain.")
  altDescription: string;
};

export type Card = {
  id: string;
  title: string;
  kind: CardKind;
  specialType?: string;
  acquisitionLocked?: boolean;
  img?: string;
  subtitle?: string;
  tag?: string;
  description?: string;
  // Normal description without any Addendum clause; used for parsing base effects.
  baseDescription?: string;
  cost: { hp: number; sp: number };
  // Some cards have a variable SP cost ("X") chosen at play time.
  // We keep the numeric cost.sp field for escalation (+N on reshuffles).
  // When true, the card's printed SP cost should be displayed as "X".
  spCostIsX?: boolean;
  // Some cards have a variable HP cost ("X") chosen at play time.
  // We keep the numeric cost.hp field for escalation (+N on reshuffles).
  // When true, the card's printed HP cost should be displayed as "X".
  hpCostIsX?: boolean;
  effects: Effect[];
  keywords: string[];
  addendum?: Addendum;
  // If > 0, the (non-cost) effects of this card trigger after N player turns.
  // Costs are still paid immediately. Turn-flow keywords (Chain/Finisher) still
  // affect the current turn as usual.
  delayTurns?: number;

  // If > 0, the BASE cost (HP/SP) of this card is paid after N player turns.
  // The card's effects still resolve immediately when played.
  // Used by cards like "Borrowed Dawn".
  costDelayTurns?: number;
  // Some cards describe multiple delayed segments (e.g. "Deal 40 damage. Delay (1). Deal 80 damage. Delay (2).").
  // In that case, each segment is scheduled separately and `effects` contains only immediate effects.
  delayed?: { turns: number; effects: Effect[] }[];
  // Effects that trigger when this card is discarded from hand (via discard mechanics), not when played.
  // These are parsed from clauses like: "If this card is discarded, ..." or "Discard: ...".
  onDiscard?: { effects: Effect[]; delayed?: { turns: number; effects: Effect[] }[] };
  // True if this card was drawn via a "Draw X" effect (a temporary bonus draw).
  // Bonus-drawn cards do NOT trigger an automatic replacement draw when played.
  bonusDraw?: boolean;
  // Some cards say "<Card> is not discarded after played." When true, the card
  // stays in hand after being played and is NOT moved to discard. It can only
  // leave the hand if another effect forces a discard.
  retainAfterPlay?: boolean;
};

export type DelayedBundle = {
  id: string;
  sourceTitle: string;
  sourceKind: CardKind;
  turnsLeft: number;
  effects: Effect[];
  owner?: 'player' | 'enemy';

  // Optional metadata to distinguish delayed *cost* payments from delayed *effects*.
  // This allows the engine to log and apply certain relic rules correctly.
  meta?: { type: 'cost'; payHp?: number; paySp?: number };
};

export type Fighter = {
  name: string;
  hp: number;
  sp: number;
  armor: number;
  stunned: number; // turns remaining
  // Optional max HP snapshot for combat logic / AI heuristics.
  maxHp?: number;
  // Reflects a % of the next damage you would take. Consumed on trigger.
  reflectPct: number;
  // Reduces the next incoming damage to 0 (consumed on first incoming hit).
  negateNextDamage: number;
};

export type Phase = 'loading' | 'setup' | 'player' | 'enemy' | 'reward' | 'gameover';

export type HandState = {
  main: { physical: Card | null; magic: Card | null };
  extra: Card[];
};

export type EnemyOngoingVenom = {
  active: boolean;
  played: boolean;
  ticksResolved: number;
  firstTickDamage: number;
  damageStepPerTurn: number;
};

export type ChymistCompoundId = 'caustic' | 'mindrot' | 'iron' | 'catalyst' | 'sedative';
export type LampKeeperRoomLightTier = 'dark' | 'steady' | 'bright';
export type LampKeeperDisposition = 'night_watch' | 'tended_flame' | 'open_flame';

export type GameState = {
  phase: Phase;
  turn: number;
  // Number of cards the player has played in the current player turn.
  playerPlaysThisTurn: number;
  playerBlackCardPlaysThisTurn?: number;
  // Bonus damage applied to the next Attack-tagged card played this turn.
  // Unlike older behavior, this is only consumed when an Attack is actually played.
  nextAttackBonus: number;
  // Tracks the relic-provided portion of `nextAttackBonus` so the correct trinket can
  // glow when that pending bonus is actually spent on an Attack.
  relicNextAttackBonus?: number;
  // If > 0, the next Attack-tagged card played ignores enemy Armor (consumed on Attack play).
  nextAttackIgnoreArmor: number;
  // If > 0, the next successful card play has its HP/SP cost reduced to 0.
  nextCardCost0: number;
  // Flat cost reduction applied to Utility-tagged cards for the current player turn.
  utilityCostReductionThisTurn?: number;
  // If > 0, the next Magic card the player successfully plays costs double SP (consumed on Magic play).
  nextMagicCardCostDoubleSP: number;
  // If > 0, the player's card effects are weakened for this many remaining player turns.
  playerWeakenedTurns: number;
  // Threshold Verifier debuff: lose 5 HP whenever you play a black card.
  // for this many remaining player turns.
  punitiveMeasureTurns?: number;
  // Threshold Verifier debuff: specific player cards that cannot be played for N remaining player turns.
  unauthorizedPassageLocks?: Array<{ cardId: string; turns: number }>;

  // If > 0, enemy damage is multiplied by `enemyDamageMult` for this many remaining enemy turns.
  // (Counts down after each enemy turn completes.)
  enemyDamageMultTurns?: number;
  // Enemy outgoing damage multiplier applied while enemyDamageMultTurns > 0.
  enemyDamageMult?: number;
  // Multiplier applied to damage dealt BY the player this turn (resets at start of player turn).
  playerDamageMultThisTurn: number;
  // Multiplier applied to damage dealt BY the player on their NEXT player turn (consumed at that next player turn start).
  playerDamageMultNextTurn: number;
  // Multiplier applied to damage dealt TO the player on the next enemy turn (resets at next player turn start).
  playerIncomingDamageMultNextTurn: number;

  // Blackhand-specific persistent debuffs / turn-flow state.
  playerMarkedByBlackhand?: boolean;
  // Player turn index when Mark was last applied. Used so Execute can distinguish
  // same-turn Mark from a Mark that was already present before this turn.
  playerMarkedTurn?: number | null;
  // If equal to the current player-turn index, Blackhand Shadowstep is active and
  // will evade the first Attack card the player successfully plays this turn.
  blackhandShadowstepTurn?: number | null;
  // If equal to the current player-turn index, Pressure Point is active for that turn.
  blackhandPressurePointTurn?: number | null;
  // How many enemy actions remain in the current enemy turn.
  enemyActionsRemainingThisTurn?: number;
  // Bonus actions to add when the next enemy turn begins.
  enemyBonusActionsNextTurn?: number;

// Tracks how much SP the player has paid this turn only from magic chain overload.
playerMagicChainOverloadSpThisTurn?: number;
// Set to true when the player used Focus / Skip this turn.
playerFocusedThisTurn?: boolean;
// Snapshot of the just-finished player turn for enemy AI.
lastPlayerTurnPlays?: number;
lastPlayerTurnBlackCardPlays?: number;
lastPlayerTurnMagicChainOverloadSp?: number;
lastPlayerTurnFocusedOnly?: boolean;
lastPlayerTurnAttacked?: boolean;
// Damage the player dealt to the enemy during the current player turn / previous one.
playerTurnDamageToEnemy?: number;
lastPlayerTurnDamageToEnemy?: number;
// Simple enemy AI memory for sequencing across turns.
enemyAiCurrentTurnActions?: string[];
enemyAiLastTurnActions?: string[];
enemyAiCombatHistory?: string[];

  // If > 0, the next time the player reflects damage, gain SP equal to the reflected damage.
  // Consumed when a reflect trigger happens.
  nextReflectGainSp?: number;


  // If > 0, the next time the player reflects damage, it triggers an additional time.
  // For example, 1 => reflect triggers twice.
  // Consumed when a reflect trigger happens.
  nextReflectTriggerTwice?: number;

  // Combat modifiers provided by the dungeon (relics/trinkets, etc.).
  relicMods?: RelicCombatMods;

  /**
   * Temporary Max HP bonus that lasts only for the current combat.
   * Applied on top of relicMods.capHp and cleared when combat ends.
   */
  tempMaxHpBonus?: number;

  /**
   * Lifesteal-like buff: heal this many HP for each point of damage the player deals.
   * Resets at end of the player's turn.
   */
  healPerDamageDealtThisTurn?: number;
  /**
   * Tracks whether the player attacked during the current player turn.
   * Used by persistent enemy effects like Dark Apostle's Cursed Fate.
   */
  playerAttackedThisTurn?: boolean;
  /**
   * Persistent Dark Apostle debuff: if true, the player takes damage and SP burn
   * whenever they end a turn without attacking. Cleared the moment they attack.
   */
  darkApostleCursedFateActive?: boolean;
  /**
   * Currently active Chymist compound, if any.
   */
  activeCompound?: ChymistCompoundId | null;
  /**
   * Tracks whether the Chymist has already spent its mandatory opening Compound play.
   */
  chymistOpeningCompoundPlayed?: boolean;
  /**
   * When true, the Chymist's next action is forced to be a random Compound.
   */
  chymistForceNextCompound?: boolean;
  /** Mercenary setup buff consumed by Deadeye Shot. */
  mercenaryPrecision?: boolean;
  /** Mercenary setup buff consumed by Gutting Strike. */
  mercenaryAmbush?: boolean;
  /** Lamp Keeper room-light state for the current combat. */
  lampKeeperRoomLight?: LampKeeperRoomLightTier;
  /** Permanent Lamp Keeper opening disposition chosen at combat start. */
  lampKeeperDisposition?: LampKeeperDisposition | null;
  /** First raise trigger already consumed this round. */
  lampKeeperRaisedLightThisRound?: boolean;
  /** First lower trigger already consumed this round. */
  lampKeeperLoweredLightThisRound?: boolean;
  /** Custodian timed discard-to-exile window measured in remaining player turns. */
  custodianForfeitureTurns?: number;
  custodianExiledAttackCount?: number;
  custodianExiledDefenseCount?: number;
  custodianExiledUtilityCount?: number;
  custodianTrackedExiledCards?: Card[];
  player: Fighter;
  // Which enemy is currently being fought. Used to rebuild enemy stats when combat starts.
  enemyId: string;
  // Base HP snapshot for the current enemy combat, before temporary damage.
  enemyMaxHpBase?: number;
  enemy: Fighter;
  // Selected deck list from the deck builder (unshuffled, combined)
  pool: Card[];

  // Unified player combat piles.
  deck: Card[];
  discard: Card[];
  exhaust: Card[];

  // Compatibility views filtered by card kind.
  physicalDeck: Card[];
  magicDeck: Card[];
  physicalDiscard: Card[];
  magicDiscard: Card[];
  physicalEscalation: number;
  magicEscalation: number;
    // Consecutive Magic cards already played in the current uninterrupted chain.
  // The next Magic card pays this much extra SP.
  // Resets on any Physical play and on new turns.
  magicChainStreak: number;
  enemyLibrary: Card[];
  enemyDeck: Card[];
  guiltTemplate?: Card | null;
  enemyForcedQueue: Card[];
  enemyOngoingVenom?: EnemyOngoingVenom;
  hand: HandState;
  enemyIntent?: Card;
  enemyIntentPreview?: Card;
  // When true, the enemy has revealed a card and we're waiting for the player
  // to acknowledge it (click or timeout) before applying its effects.
  enemyAwaitingAck: boolean;
  // Internal turn-handoff flag: after the player ends their turn, enemy resolution
  // should begin automatically once UI/animation gating allows it.
  enemyStartPending?: boolean;
  // Player may play a card only if true. After each play, this becomes
  // true only when the last played card had "Chain" (unless it had "Finisher").
  canPlay: boolean;
  // When true, the UI should wait for combat feedback/animations to finish, then
  // automatically trigger Focus and end the turn. Used when a non-physical play
  // leaves the player with no cards in hand.
  autoFocusPending?: boolean;
  // When non-null, the player must discard `remaining` cards from hand before
  // continuing the turn. Stores the played card's Chain/Finisher behavior to
  // resume correctly after discarding.
  pendingDiscard: null | { remaining: number; chain: boolean; finisher: boolean; sourceKind?: 'physical' | 'magic'; afterDraw?: { sameDeck: number; sameDeckPhysical?: number; sameDeckMagic?: number; sameDeckAny?: number; random: number; randomPhysical: number; randomMagic: number }; resumeEnemyTurn?: boolean };
  // When non-null, the player must reorder revealed cards from the player deck before continuing.
  pendingPeek: null | { kind: 'physical' | 'magic' | 'any'; cards: Card[]; sourceKind?: 'physical' | 'magic'; after: { discard: number; chain: boolean; finisher: boolean; afterDraw?: { sameDeck: number; sameDeckPhysical?: number; sameDeckMagic?: number; sameDeckAny?: number; random: number; randomPhysical: number; randomMagic: number } } };
// When non-null, the player must pick one of the revealed top cards (e.g. Third Eye),
// then the rest are discarded.
pendingTopPick: null | { kind: 'physical' | 'magic' | 'any'; cards: Card[]; sourceKind?: 'physical' | 'magic'; after: { discard: number; chain: boolean; finisher: boolean; afterDraw?: { sameDeck: number; sameDeckPhysical?: number; sameDeckMagic?: number; sameDeckAny?: number; random: number; randomPhysical: number; randomMagic: number } } };

  // When non-null, the player must choose one of multiple effect options before continuing (e.g. Mysterious Liquid).
  pendingChoice: null | { sourceTitle: string; sourceId?: string; sourceKind?: CardKind; sourceOwner?: 'player' | 'enemy'; options: ChooseOneOption[]; after: { discard: number; chain: boolean; finisher: boolean } };
  pendingEnemyResolution?: null | { consumesAction?: boolean };
  // When non-null, the player must pick a specific card from a deck and add it to hand (e.g. Shady Tutor / Obscure Melody / Spell Book).
  // kind:
  //  - physical/magic: search only that deck
  //  - any: search both decks (physical + magic)
  pendingTutor: null | { kind: 'physical' | 'magic' | 'any'; zone?: 'deck' | 'discard'; sourceKind?: 'physical' | 'magic'; after: { discard: number; chain: boolean; finisher: boolean } };
  // When non-null, the player must choose a numeric X value (e.g. Blood Ritual / Forbidden Knowledge).
  pendingX: null | { sourceTitle: string; from: 'hp' | 'sp'; to: 'hp' | 'sp'; max: number; sourceKind?: 'physical' | 'magic'; after: { discard: number; chain: boolean; finisher: boolean } };
  // Post-victory reward: three random cards (not already in deck) to choose from.
  rewardChoices: Card[];
  // Scheduled effects that trigger at the start of the player's turn.
  delayed: DelayedBundle[];
  // When a card with a discard prerequisite has been selected and the player is
  // currently paying that discard cost before the card actually commits, we keep
  // the pending play payload here so UI can finish the discard step and then run
  // the normal play animation/commit.
  pendingPrereqPlay: null | { cardId: string; addendumPaySp?: number; addendumPayHp?: number; xPaySp?: number; xPayHp?: number };
  log: string[];
  error?: string;
  uiEvents: CombatUiEvent[];

  /**
   * Set to true if the player has taken any HP damage during the current combat. Used by relics like Untarnished Candle.
   */
  playerTookDamage?: boolean;
  /**
   * Set to true if the player has taken any enemy damage during the current combat, including Armor loss.
   */
  playerTookEnemyDamageThisCombat?: boolean;
  /**
   * Set to true if the enemy was defeated by exact lethal damage (no overkill). Used by relics like Zero Fang (Ivory Tooth).
   */
  enemyKilledExactlyAtZero?: boolean;
  /**
   * Snapshot of HP at the instant combat ends (before end-of-combat relic restores).
   */
  endCombatHp?: number;
  /**
   * Snapshot of SP at the instant combat ends (before end-of-combat relic restores).
   */
  endCombatSp?: number;
  /**
   * Number of Addendum card plays this combat.
   */
  addendumPlayedCountThisCombat?: number;
  /**
   * Set to true once the player pays an Addendum cost during the current combat. Used by relics like Sealed Receipt.
   */
  paidAddendumCostsThisCombat?: boolean;
  /**
   * List of Addendum card IDs that entered the player's hand at least once this combat.
   * Used by relics like Red Ledger Seal / Sealed Receipt.
   */
  addendumSeenThisCombat?: string[];
  /**
   * List of Addendum card IDs that were played at least once this combat.
   * Used by relics like Red Ledger Seal / Sealed Receipt.
   */
  addendumPlayedThisCombat?: string[];
  /**
   * Becomes false if ANY played Addendum card was played without using Addendum (i.e. without paying its Addendum cost).
   * Used by relics like Red Ledger Seal / Sealed Receipt.
   */
  paidAllAddendumCostsThisCombat?: boolean;
  /**
   * Set to true once the player has played their first card with the Exile keyword this combat. Used by relics like First Exile Seal.
   */
  firstExileCardPlayed?: boolean;
  /**
   * Tracks how many cards the player has drawn in the current player turn. Used by relics like Dealing Die.
   */
  drawnThisTurn?: number;
  /**
   * Temporary setup flag used so opening-hand draws do not count as in-turn draws.
   */
  suppressDrawTrackingThisTurn?: boolean;
  /**
   * Damage reduction applied to incoming damage if the player's deck has 30 or more cards and the appropriate relic is owned.
   */
  damageReductionIfLargeDeck?: number;
  /**
   * Delay reduction applied to delayed effect bundles if the player's deck has 30 or more cards and the appropriate relic is owned.
   */
  reduceDelayForLargeDeck?: number;
  /**
   * Number of active Overflow Burst effects this combat. Each one converts
   * excess Reflect above cap into damage when Reflect is gained.
   */
  reflectOverflowBurstStacks?: number;
  /**
   * Number of Fate Reversed effects active for the current player turn.
   * Each stack replays discarded cards once when they are discarded from hand.
   */
  discardPlayThisTurn?: number;
  monsignorDecree?: { id: string; text: string; broken: boolean; breakReason?: string } | null;
  monsignorSpSpentThisTurn?: number;
  monsignorPlayedCardTypesThisTurn?: string[];
};

export type RelicCombatMods = {
  // Flat bonuses applied at combat start.
  startArmor?: number;
  startHp?: number;
  startSp?: number;

  /**
   * Bonus armor granted to the enemy at the start of combat. Added to the
   * enemy's base armor defined in the enemy roster. This is used by the
   * tarot mechanic (Iron Banner reversed) to give enemies an advantage
   * on the current floor.
   */
  enemyStartArmor?: number;

  /**
   * Flat HP adjustment applied to the enemy at the start of combat. This can be
   * negative (for example, when dungeon-side events weaken a boss before the fight).
   */
  enemyStartHp?: number;

  // If > 0, the first successful card play this combat has its cost reduced to 0.
  startNextCardCost0?: number;

  // Start-of-combat defensive setup.
  // Reflect % applies to the NEXT incoming hit (consumed on trigger).
  startReflectPct?: number;
  // Number of incoming hits to negate entirely (consumed per hit).
  startNegateNextDamage?: number;

  // Applied at the start of EACH player turn.
  turnStartAttackBonus?: number;
  // Flat Armor gained at the start of EACH player turn.
  turnStartArmor?: number;

  // Extra random draws applied at combat start (goes into the extra hand slots, obeying hand cap).
  firstTurnExtraDraw?: number;

  // Exploration torch percentage forwarded into Lamp Keeper fights so Room Light can derive from dungeon state.
  lampKeeperInitialTorchProgress?: number;

  // --- Custom relic effect fields (added to support new relics) ---
  /**
   * Gain this much SP whenever you discard a card. For relics like Dull Shiv.
   */
  onDiscardGainSp?: number;
  /**
   * Gain this much HP whenever you discard a card. For relics like Brine Vial.
   */
  onDiscardGainHp?: number;
  /**
   * At the end of your player turn, if you have no cards in hand, draw this many cards.
   */
  endTurnEmptyDraw?: number;
  /**
   * At the end of your player turn, if you have exactly five cards in hand, gain this much Armor.
   */
  endTurnFiveCardsArmor?: number;
  /**
   * At the end of your player turn, gain this much Armor per card remaining in your hand.
   */
  endTurnArmorPerCard?: number;
  /**
   * If true, double any Armor gained from card effects. For Double‑Plated Cuirass.
   */
  doubleArmorGainFromCards?: boolean;
  /**
   * Flat bonus damage added to every Attack card played.
   */
  attackBonusFlat?: number;
  /**
   * Additional damage added to Attack effects when the player's SP is at or below a threshold.
   */
  attackBonusWhenLowSp?: number;
  /**
   * SP threshold for attackBonusWhenLowSp to apply.
   */
  lowSpThreshold?: number;
  /**
   * Gain this much HP whenever you reflect damage back to the enemy. For Mirror Rosary.
   */
  reflectBonusHp?: number;
  /**
   * Gain this much SP whenever you reflect damage back to the enemy. For Mercury Locket.
   */
  reflectBonusSp?: number;
  /**
   * Draw this many cards (max hand size permitting) whenever you reflect damage. For Quicksilver Lens.
   */
  reflectBonusDraw?: number;
  /**
   * When true, the player cannot be stunned by enemy effects. For Lead Ribbon.
   */
  immuneStun?: boolean;
  /**
   * Additional SP gained when using Focus / skipping the turn.
   */
  focusBonusSP?: number;
  /**
   * HP gained when using Focus / skipping the turn.
   */
  focusBonusHP?: number;
  /**
   * If true, healing above max HP becomes Armor, and Armor gained above cap becomes SP.
   */
  invertedHeart?: boolean;
  /**
   * Overrides the normal Recast redraw penalty. Base behavior is 1.
   * A value of 0 means Recast redraws the same number of cards that were selected.
   */
  recastDrawPenalty?: number;
  /**
   * Prime Vessel device-derived combat flags. Passed from the dungeon layer when that boss starts.
   */
  primeVesselPulseEngineActive?: boolean;
  primeVesselSynapticRetortActive?: boolean;
  primeVesselAstralReliquaryActive?: boolean;
  primeVesselPrimeCrucibleActive?: boolean;
  /**
   * Gain this much Armor whenever you play a card that has a Delay keyword or delayTurns. For Tardy Bracer.
   */
  delayCardArmor?: number;
  /**
   * Reduce the SP cost of cards with Delay by this amount. For Lightened Buckle.
   */
  delayCostReduction?: number;
  /**
   * Gain this much SP whenever your SP transitions to exactly zero. For Zero Key.
   */
  spGainWhenZero?: number;
  /**
   * Reduce Addendum costs by this amount of SP. For Muted Inkwell.
   */
  addendumCostReduction?: number;
  /**
   * Minimum number of Addendum card plays required before the end-of-combat SP reward can trigger.
   */
  gainSpOnPayAddendumThreshold?: number;
  /**
   * SP restored at the end of combat when the Addendum threshold condition is met.
   */
  gainSpAmount?: number;
  /**
   * Reduce the damage taken by the player by this amount if their deck has 30 or more cards at combat start. For Ballast Shield.
   */
  damageReductionIfLargeDeck?: number;
  /**
   * Reduce the delay of all delayed effects (number of turns) by this amount if the deck has 30 or more cards at combat start. For Fine Sand Hourglass.
   */
  reduceDelayForLargeDeck?: number;
  /**
   * Gain this much SP for each card drawn beyond the first in a player turn. For Dealing Die.
   */
  drawBeyondFirstGainSp?: number;
  /**
   * Additional damage added to Attack effects when the player's Armor is zero. For Bare Blade.
   */
  attackBonusWhenArmorZero?: number;
  /**
   * Reduce the SP cost of cards with the Chain keyword by this amount. For Light Link.
   */
  chainCostReduction?: number;
  /**
   * When true, preview the enemy's next intended action during the player's turn.
   */
  revealEnemyIntentPreview?: boolean;
  /**
   * When true, the first card with Exile keyword played each combat will not Exile itself. For First Exile Seal.
   */
  firstExileDontExile?: boolean;
  /**
   * Amount of extra Teeth gained when defeating an enemy with their HP ending exactly at zero. For Ivory Tooth.
   */
  ivoryTooth?: number;
  /**
   * If true, double loot quantity from combat when SP is at or below threshold. (Legacy/compat)
   */
  brinkShrineDoubleLoot?: boolean;
  /**
   * Flat loot quantity bonus to apply to combat loot when SP is at or below threshold.
   */
  brinkShrineLootQuantityBonus?: number;
  /**
   * Amount of HP gained per card that cannot be drawn because the player's hand is full. For Bottomless Pouch.
   */
  fullHandGainHpOnDraw?: number;
  /**
   * The HP penalty to apply when a player loses no HP by the end of combat, as per Untarnished Candle. If this is defined and the player took no damage, this amount is subtracted from HP at combat end.
   */
  untarnishedCandleLoseHp?: number;
  /**
   * Teeth gained when the Untarnished Candle penalty triggers. For Untarnished Candle.
   */
  untarnishedCandleTeeth?: number;
  /**
   * When true, all cards with the Exile keyword also have Chain. For Exile Chain.
   */
  exileCardsGetChain?: boolean;
  /**
   * Optional UI bridge: maps combat trigger categories back to one or more owned relic ids
   * so the matching trinket icon can glow when its effect fires.
   */
  feedbackRelicIdByTrigger?: Partial<Record<RelicFeedbackTriggerKey, string | string[]>>;
  /**
   * UI-only: per-trigger per-relic breakdown used to show the correct value next to the
   * correct trinket when multiple relics contribute to the same combined combat modifier.
   */
  feedbackRelicEntriesByTrigger?: Partial<Record<RelicFeedbackTriggerKey, RelicFeedbackEntry[]>>;
  // Additional mod fields can be added here as needed for future relics.
  /**
   * UI-only: the pure relic-only start HP contribution, without dungeon-entry offsets.
   */
  feedbackStartHp?: number;

  /**
   * UI-only: the pure relic-only start SP contribution, without dungeon-entry offsets.
   */
  feedbackStartSp?: number;
};


function stripOnDiscardTriggerClauses(desc: string): string {
  if (!desc) return '';
  const s = String(desc).replace(/\r\n/g, '\n');
  const paragraphs = s.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  const keptParagraphs: string[] = [];

  for (const para of paragraphs) {
    const normalized = para
      .replace(/\.{2,}/g, '.')
      .replace(/([.!?])/g, '$1\n');

    const clauses = normalized
      .split(/\n+/)
      .map((c) => c.trim())
      .filter(Boolean);

    const kept: string[] = [];
    for (let i = 0; i < clauses.length; i++) {
      const clause = clauses[i];

      const isTrigger =
        /^(?:If|When)\s+this\s+card\s+is\s+discarded\s*(?:,|:)?\s*.+$/i.test(clause) ||
        /^Discard\s*:\s*.+$/i.test(clause);

      if (!isTrigger) {
        kept.push(clause);
        continue;
      }

      // If the trigger is followed by a Delay marker in the same paragraph, treat the rest of the paragraph
      // as part of the discard-trigger block, so delayed clauses don't leak into on-play parsing.
      const hasDelayAfter = clauses.slice(i + 1).some((c) => /^Delay\s*\(/i.test(c));
      if (hasDelayAfter) break; // drop trigger + tail of paragraph
      // Otherwise, drop only the trigger clause and keep parsing the rest.
    }

    const rebuilt = kept.join(' ').replace(/\s{2,}/g, ' ').trim();
    if (rebuilt) keptParagraphs.push(rebuilt);
  }

  return keptParagraphs.join('\n\n').trim();
}


function extractOnDiscardTriggers(args: {
  title: string;
  subtitle: string;
  kind: CardKind;
  desc: string;
}): { cleaned: string; onDiscard?: { effects: Effect[]; delayed?: { turns: number; effects: Effect[] }[] } } {
  const subtitle = args.subtitle ?? '';
  const s = String(args.desc ?? '').replace(/\r\n/g, '\n');
  if (!s) return { cleaned: '' };

  const paragraphs = s.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  const keptParagraphs: string[] = [];
  const effects: Effect[] = [];
  const delayed: { turns: number; effects: Effect[] }[] = [];

  for (const para of paragraphs) {
    const normalized = para
      .replace(/\.{2,}/g, '.')
      .replace(/([.!?])/g, '$1\n');

    const clauses = normalized
      .split(/\n+/)
      .map((c) => c.trim())
      .filter(Boolean);

    const kept: string[] = [];

    for (let i = 0; i < clauses.length; i++) {
      const clause = clauses[i];

      let body: string | null = null;

      const mIf = clause.match(/^(?:If|When)\s+(?:this\s+card\s+is\s+)?discarded\s*(?:,|:)?\s*(.+)$/i);
      if (mIf && mIf[1]) body = mIf[1].trim();

      if (!body) {
        const mColon = clause.match(/^Discard\s*:\s*(.+)$/i);
        if (mColon && mColon[1]) body = mColon[1].trim();
      }

      if (body) {
        const tail = clauses.slice(i + 1);
        const hasDelayAfter = tail.some((c) => /^Delay\s*\(/i.test(c));

        const segmentClauses = [body, ...(hasDelayAfter ? tail : [])];
        const segment = segmentClauses.join(' ').replace(/\s{2,}/g, ' ').trim();

        const parsed = inferEffectsWithDelaySequence(args.title, subtitle, segment, args.kind);
        if (parsed.effects.length > 0) effects.push(...parsed.effects);
        if (parsed.delayed.length > 0) delayed.push(...parsed.delayed);

        if (hasDelayAfter) break; // consumed rest of paragraph
        continue; // keep parsing rest of paragraph
      }

      kept.push(clause);
    }

    const rebuilt = kept.join(' ').replace(/\s{2,}/g, ' ').trim();
    if (rebuilt) keptParagraphs.push(rebuilt);
  }

  const cleaned = keptParagraphs.join('\n\n').trim();
  if (effects.length === 0 && delayed.length === 0) return { cleaned };
  return {
    cleaned,
    onDiscard: {
      effects,
      delayed: delayed.length > 0 ? delayed : undefined,
    },
  };
}


function mergeOnDiscard(
  a?: { effects: Effect[]; delayed?: { turns: number; effects: Effect[] }[] },
  b?: { effects: Effect[]; delayed?: { turns: number; effects: Effect[] }[] }
): { effects: Effect[]; delayed?: { turns: number; effects: Effect[] }[] } | undefined {
  const ae = a?.effects ?? [];
  const be = b?.effects ?? [];
  const ad = a?.delayed ?? [];
  const bd = b?.delayed ?? [];
  const effects = [...ae, ...be];
  const delayed = [...ad, ...bd];
  if (effects.length === 0 && delayed.length === 0) return undefined;
  return {
    effects,
    delayed: delayed.length > 0 ? delayed : undefined,
  };
}

function extractCostDelayClause(desc: string): { cleaned: string; costDelayTurns: number } {
  let cleaned = String(desc ?? '');
  let costDelayTurns = 0;

  // Example text:
  // "Gain 50 SP. The cost of this card has Delay (1)."
  // This Delay marker should NOT delay the card's effects; it delays the *cost payment*.
  const re = /(?:^|\b)(?:The\s+)?cost\s+of\s+this\s+card\s+has\s+Delay\s*\(\s*([A-Za-z0-9-]+)\s*\)\s*\.?/i;
  const m = cleaned.match(re);
  if (m) {
    const t = parseCountToken(String(m[1] ?? '').trim());
    costDelayTurns = Math.max(0, Number(t ?? 0) || 0);
    cleaned = cleaned.replace(m[0], ' ');
  }

  // IMPORTANT: preserve paragraph boundaries (\n\n) so Delay markers only apply to effects
  // within their own block (e.g. "Deal 20 damage.<p>Deal 20 damage. Delay (1)</p>").
  cleaned = cleaned.replace(/\r\n/g, '\n');

  const paragraphs = cleaned
    .split(/\n{2,}/)
    .map((p) =>
      p
        .replace(/\n/g, ' ')
        .replace(/[\t ]{2,}/g, ' ')
        .replace(/\s+\./g, '.')
        .replace(/\.{2,}/g, '.')
        .trim()
    )
    .filter(Boolean);

  cleaned = paragraphs.join('\n\n').trim();
  return { cleaned, costDelayTurns };
}


export function normalizeCard(raw: RawCard): Card {
  const id = String(raw.id ?? raw._id ?? `${raw.title ?? raw.name ?? 'card'}-${Math.random().toString(16).slice(2)}`);
  const title = String(raw.title ?? raw.name ?? 'Untitled');
  const subtitle = raw.subtitle ?? '';
  // Some backends send HTML in descriptions (e.g. "<b>Discard</b> one card").
  // Strip tags + decode common entities so keyword/effect parsing is robust,
  // and the UI doesn't show raw HTML markup.
  const desc = toPlainText(raw.description ?? '');
  const add = extractAddendum(desc);
  const baseDesc = add ? add.baseDescription : desc;
  const primeVesselBaseDesc = getPrimeVesselBaseDescription(title);
  const parseDesc = primeVesselBaseDesc ?? baseDesc;
  const cardType = (raw.cardType ?? '').toLowerCase();
  const subtitleLower = String(subtitle ?? '').toLowerCase();
  const rawDeckKind = String((raw as any).deckKind ?? '').trim().toLowerCase();
  const inferredSpecialType = String((raw as any).specialType ?? (cardType === 'affliction' || subtitleLower.includes('affliction') ? 'affliction' : '')).trim().toLowerCase();
  const kind: CardKind = subtitleLower.includes('enemy')
    ? 'enemy'
    : (rawDeckKind === 'magic' || cardType === 'magic' || inferredSpecialType === 'affliction' ? 'magic' : 'physical');

  // Card classification tag used by several mechanics (e.g. "Attack" for Dual Wielding).
  // Some backends store this as a dedicated field (tag/cardTag/typeTag), while others
  // embed it in the subtitle like "· Attack ·".
  let tag = String((raw as any).tag ?? (raw as any).cardTag ?? (raw as any).typeTag ?? '').trim();
  if (!tag) {
    const s = String(subtitle ?? '').toLowerCase();
    if (inferredSpecialType === 'affliction') tag = 'Affliction';
    else if (s.includes('attack')) tag = 'Attack';
    else if (s.includes('defense')) tag = 'Defense';
    else if (s.includes('utility')) tag = 'Utility';
  }

  const rawHp = (raw as any).hpCost ?? 0;
  const rawSp = (raw as any).spCost ?? 0;
  const hpIsX = typeof rawHp === 'string' && String(rawHp).trim().toLowerCase() === 'x';
  const hp = hpIsX ? 0 : (Number(rawHp ?? 0) || 0);
  const spIsX = typeof rawSp === 'string' && String(rawSp).trim().toLowerCase() === 'x';
  const sp = spIsX ? 0 : (Number(rawSp ?? 0) || 0);

  const img = raw.cardUrl || raw.imageUrl || raw.img || raw.image;

  const keywords = normalizeKeywords(primeVesselBaseDesc ? [] : raw.keywords, parseDesc);
  // Extract discard-triggered effects ("If this card is discarded, ..." / "Discard: ...")
  // so they don't run on play.
  const discBase = extractOnDiscardTriggers({ title, subtitle, kind, desc: parseDesc });
  const discAlt = add ? extractOnDiscardTriggers({ title, subtitle, kind, desc: add.addendum.altDescription }) : { cleaned: '', onDiscard: undefined };
  const onDiscard = mergeOnDiscard(discBase.onDiscard, discAlt.onDiscard);

  // Remove "cost has Delay (X)" clauses so Delay parsing doesn't incorrectly delay the effects.
  const costDelay = extractCostDelayClause(discBase.cleaned);
  const playDesc = costDelay.cleaned;

  const parsed = inferEffectsWithDelaySequence(title, subtitle, playDesc, kind);
  let effects = parsed.effects;
  const delayed = parsed.delayed;

  // Card-specific safety: Consonance
  // "Gain 10 SP for each card played this turn. Discard a card."
  // The generic parser should already cover this, but we add a fallback so minor backend
  // text variations don't cause the card to become inert.
  if (title.trim().toLowerCase() === 'consonance') {
    const hasPer = effects.some((e: any) => (e as any)?.type === 'healSPPerPlaysThisTurn');
    const hasDiscard = effects.some((e: any) => (e as any)?.type === 'discard');

    if (!hasPer) {
      const per =
        matchNumber(
          playDesc,
          /Gain\s+(\d+)\s+(?:Sanity\s+Points|SP)\s+for\s+(?:each|every)\s+card\b[^\n.]{0,60}\bplayed\s+this\s+turn/i
        ) ??
        matchNumber(playDesc, /Gain\s+(\d+)\s+(?:Sanity\s+Points|SP)\s+for\s+each\s+card\s+played\s+this\s+turn/i);

      if (per) effects = [...effects, { type: 'healSPPerPlaysThisTurn', amountPer: per } as any];
    }

    if (!hasDiscard) {
      // Covers both "Discard a card." and "Discard 1 card." (and minor variations).
      const d = matchCount(playDesc, /Discard\s+([A-Za-z0-9-]+)\s+cards?/i) ?? 1;
      if (/\bDiscard\b/i.test(playDesc)) effects = [...effects, { type: 'discard', amount: d } as any];
    }
  }


// Card-specific safety: Second Wind
// UI text format: "Choose one: Gain 25 HP or gain 10 SP and draw an additional card next turn."
// The backend sometimes ships this as a single sentence with "or", which the generic Choose One parser
// cannot reliably split. Implement it explicitly.
if (title.trim().toLowerCase() === 'second wind') {
  effects = [
    {
      type: 'chooseOne',
      options: [
        { label: '+25 HP', effects: [{ type: 'healHP', amount: 25 } as any] },
        {
          label: '+10 SP + draw next turn',
          effects: [{ type: 'healSP', amount: 10 } as any],
          delayed: [{ turns: 1, effects: [{ type: 'drawRandom', amount: 1 } as any] }],
        },
      ],
    } as any,
  ];
  // Ensure the generic parser doesn't also schedule its own delays for this card.
  if (Array.isArray(delayed)) delayed.length = 0;
}


// Card-specific safety: Mend the Wound
// UI text format: "Choose one: Gain 40 HP or Gain 40 SP."
if (title.trim().toLowerCase() === 'mend the wound') {
  effects = [
    {
      type: 'chooseOne',
      options: [
        { label: '+40 HP', effects: [{ type: 'healHP', amount: 40 } as any] },
        { label: '+40 SP', effects: [{ type: 'healSP', amount: 40 } as any] },
      ],
    } as any,
  ];
  if (Array.isArray(delayed)) delayed.length = 0;
}


// Card-specific safety: Confidence
if (title.trim().toLowerCase() === 'confidence') {
  effects = [
    { type: 'convertArmorToSp' } as any,
    { type: 'convertReflectToHp' } as any,
  ];
  if (Array.isArray(delayed)) delayed.length = 0;
}

// Card-specific safety: Overflow Burst
if (title.trim().toLowerCase() === 'overflow burst') {
  effects = [{ type: 'enableReflectOverflowBurst' } as any];
  if (Array.isArray(delayed)) delayed.length = 0;
}

// Card-specific safety: Forsaken Tome
if (title.trim().toLowerCase() === 'forsaken tome') {
  effects = [{ type: 'tutor', kind: 'any', zone: 'discard' } as any];
  if (Array.isArray(delayed)) delayed.length = 0;
}

// Card-specific safety: Glass Reprisal
if (title.trim().toLowerCase() === 'glass reprisal') {
  effects = [{ type: 'damageEqualToReflect' } as any];
  if (Array.isArray(delayed)) delayed.length = 0;
}

// Card-specific safety: Fate Reversed
if (title.trim().toLowerCase() === 'fate reversed') {
  effects = [{ type: 'enableDiscardPlayThisTurn' } as any];
  if (Array.isArray(delayed)) delayed.length = 0;
}

// Card-specific safety: Guilt
if (title.trim().toLowerCase() === 'guilt') {
  effects = [{ type: 'loseHP', amount: 5 } as any];
  if (Array.isArray(delayed)) delayed.length = 0;
}

// Card-specific safety: Mental Grasp
// Exact intended behavior: deal 20 damage, stun for 1 turn, and discard 1 card on play.
// Keep this explicit so it works even before/if the backend text is updated.
if (title.trim().toLowerCase() === 'mental grasp') {
  effects = [
    { type: 'damage', amount: 20 } as any,
    { type: 'stun', turns: 1 } as any,
    { type: 'discard', amount: 1 } as any,
  ];
  if (Array.isArray(delayed)) delayed.length = 0;
}


  // Card-specific safety: Backstab
  // This card is a replacement condition, not an additive one:
  // base line = deal X, conditional line = deal Y instead if the opponent already has Stun.
  // Some versions also add SP gains on each branch, so keep those optional.
  if (title.trim().toLowerCase() === 'backstab') {
    const flat = playDesc.replace(/\s+/g, ' ').trim();
    const condIdx = flat.toLowerCase().search(/\bif\s+(?:the\s+)?enemy\s+has\s+stun\b/i);
    const baseText = condIdx >= 0 ? flat.slice(0, condIdx).trim() : flat;
    const condText = condIdx >= 0 ? flat.slice(condIdx).trim() : '';

    const baseDmg = matchNumber(baseText, /Deal\s+(\d+)\s+damage\b/i);
    const baseSp = matchNumber(baseText, /Gain\s+(\d+)\s+(?:Sanity\s+Points|SP)\b/i);
    const altDmg = matchNumber(condText, /deal\s+(\d+)\s+damage\b/i);
    const altSp = matchNumber(condText, /gain\s+(\d+)\s+(?:sanity\s+points|sp)\b/i);

    if (baseDmg && altDmg) {
      const thenEffects: AtomicEffect[] = [{ type: 'damage', amount: altDmg } as any];
      const elseEffects: AtomicEffect[] = [{ type: 'damage', amount: baseDmg } as any];
      if (altSp) thenEffects.push({ type: 'healSP', amount: altSp } as any);
      if (baseSp) elseEffects.push({ type: 'healSP', amount: baseSp } as any);

      effects = [
        {
          type: 'ifOpponentHasStun',
          then: thenEffects,
          else: elseEffects,
        } as any,
      ];
    } else if (baseDmg) {
      // Fallback: apply only the base line to avoid accidental additive parsing.
      effects = [{ type: 'damage', amount: baseDmg } as any];
      if (baseSp) effects.push({ type: 'healSP', amount: baseSp } as any);
    }
  }

  // Card-specific safety: Hasten the Inevitable
  // "Resolve all Delay stacks. All effects resolved this way are reduced in half. Exile."
  // This is a bespoke engine mechanic; the generic parser should not attempt to infer it.
  if (title.trim().toLowerCase() === 'hasten the inevitable') {
    effects = [{ type: 'resolveAllDelayHalved' } as any];
    // Ensure no accidental Delay parsing (e.g. if backend text changes).
    if (Array.isArray(delayed)) delayed.length = 0;
  }

  const retainAfterPlay = /\bnot\s+discarded\s+after\s+(?:being\s+)?played\b/i.test(desc)
    || /\bnot\s+discarded\s+after\s+play\b/i.test(desc)
    || /\bnot\s+discarded\s+after\s+you\s+play\b/i.test(desc);

  return {
    id,
    title,
    kind,
    specialType: inferredSpecialType || undefined,
    acquisitionLocked: !!((raw as any).acquisitionLocked),
    img,
    subtitle,
    tag: tag || undefined,
    description: desc,
    baseDescription: playDesc,
    cost: { hp, sp },
    spCostIsX: spIsX || undefined,
    hpCostIsX: hpIsX || undefined,
    effects,
    keywords,
    addendum: add ? add.addendum : undefined,
    onDiscard: onDiscard,
    delayed: delayed.length > 0 ? delayed : undefined,
    delayTurns: 0,
    costDelayTurns: costDelay.costDelayTurns > 0 ? costDelay.costDelayTurns : undefined,
    retainAfterPlay,
  };
}

// Build a play-variant from an arbitrary description string.
// Used for Addendum: the extra effect text is parsed separately and can be combined with the base effect text at play time.
export function parseVariantFromDescription(args: {
  title: string;
  subtitle?: string;
  kind: CardKind;
  description: string;
  // When provided, occurrences of standalone "X" in the description are replaced with this number.
  xValue?: number;
}): { effects: Effect[]; delayed: { turns: number; effects: Effect[] }[]; keywords: string[] } {
  const subtitle = args.subtitle ?? '';
  let desc = args.description ?? '';
  if (typeof args.xValue === 'number' && Number.isFinite(args.xValue)) {
    // Replace standalone X tokens (case-insensitive) with the numeric value.
    // Example: "Deal X damage" -> "Deal 20 damage".
    const v = String(Math.max(0, Math.floor(args.xValue)));
    desc = desc.replace(/\bX\b/gi, v);
  }
  // Avoid interpreting "cost has Delay (X)" as a delayed *effect*.
  const playDescRaw = stripOnDiscardTriggerClauses(desc);
  const costDelay = extractCostDelayClause(playDescRaw);
  const parsed = inferEffectsWithDelaySequence(args.title, subtitle, costDelay.cleaned, args.kind);
  // Keywords are inferred from the same description.
  const keywords = normalizeKeywords([], desc);
  return { effects: parsed.effects, delayed: parsed.delayed, keywords };
}

function extractAddendum(desc: string): null | { baseDescription: string; addendum: Addendum } {
  // Pattern examples:
  // "Addendum (20 SP) - Deal 35 damage instead. Chain."
  // "Addendum (X SP) - Deal X damage instead."
  // "Addendum (25 HP) - Gain 10 SP for each card you played this turn."
  const re = /Addendum\s*\(\s*([A-Za-z0-9-]+)\s*(SP|HP)\s*\)\s*(?:-|–|—)\s*/i;
  const m = desc.match(re);
  if (!m || m.index == null) return null;
  const idx = m.index;
  const before = desc.slice(0, idx).trim();
  const after = desc.slice(idx + m[0].length).trim();
  const token = String(m[1] || '').trim();
  const resource = String(m[2] || '').trim().toUpperCase();
  const cost: number | 'X' = /^x$/i.test(token) ? 'X' : (parseCountToken(token) ?? 0);
  if (cost !== 'X' && (!Number.isFinite(cost) || cost <= 0)) {
    return null;
  }
  return {
    baseDescription: before,
    addendum: {
      ...(resource === 'HP' ? { costHp: cost } : { costSp: cost }),
      altDescription: after,
    },
  };
}


function toPlainText(input: string): string {
  if (!input) return '';
  let s = String(input);
  // Normalize common line breaks first
  s = s.replace(/<\s*br\s*\/?>/gi, '\n');
  // Treat <p> blocks as *paragraph* boundaries. We use a DOUBLE newline so the
  // downstream Delay parser can reliably split paragraphs even if the backend
  // emits compact HTML like "</p><p>" or mixes text outside <p>.
  s = s.replace(/<\s*\/?p\b[^>]*>/gi, '\n\n');
  // Remove all remaining HTML tags
  s = s.replace(/<[^>]*>/g, '');

  // Decode a few common entities (keep it lightweight; no DOMParser in node)
  const named: Record<string, string> = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
  };
  s = s.replace(/&(nbsp|amp|lt|gt|quot);|&#39;/g, (m) => named[m] ?? m);

  // Numeric entities: &#123; or &#x1F4A9;
  s = s.replace(/&#(\d+);/g, (_, n) => {
    const code = parseInt(n, 10);
    return Number.isFinite(code) ? String.fromCodePoint(code) : _;
  });
  s = s.replace(/&#x([0-9a-fA-F]+);/g, (_, n) => {
    const code = parseInt(n, 16);
    return Number.isFinite(code) ? String.fromCodePoint(code) : _;
  });

  // Cleanup whitespace
  s = s.replace(/\r\n/g, '\n');
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
}


function getPrimeVesselBaseDescription(title: string): string | null {
  const t = String(title ?? '').trim().toLowerCase();
  switch (t) {
    case 'corruption surge':
      return 'Deal 30 damage. Delay (1).';
    case 'containment breach':
      return 'Deal 10 damage. Delay (1).';
    case 'protection protocol':
      return 'Gain 30 Armor. Delay (1).';
    case 'ruptured ascension':
      return 'Gain 20 HP. Delay (1).';
    default:
      return null;
  }
}

function normalizeKeywords(k: RawCard['keywords'], desc: string): string[] {
  const out: string[] = [];
  if (Array.isArray(k)) out.push(...k.map(String));
  if (typeof k === 'string' && k.trim()) out.push(k.trim());
  // fallback heuristic
  if (/exile/i.test(desc)) out.push('Exile');
  if (/\bexhaust\b/i.test(desc)) out.push('Exile');
  if (/remove\s+this\s+card\s+from\s+the\s+game/i.test(desc)) out.push('Exile');
  if (/\bchain\b/i.test(desc)) out.push('Chain');
  if (/\bfinisher\b/i.test(desc)) out.push('Finisher');
  if (/\bdiscard\b/i.test(desc)) out.push('Discard');
  if (/\bdraw\b/i.test(desc)) out.push('Draw');
  return Array.from(new Set(out.map(s => s.trim()).filter(Boolean)));
}


// Some cards have a "Choose one" structure.
// We support a generic pattern:
// "Choose one: <Option A>. <Option B>. <Option C>."
// Each option is parsed using the same effect inference as normal text segments.
// NOTE: Keywords inside options (e.g. "Chain") are ignored for now — options apply effects only.
function extractChooseOne(desc: string, kind: CardKind): null | { type: 'chooseOne'; options: ChooseOneOption[] } {
  if (!/\bChoose\s+one\b/i.test(desc)) return null;

  // Grab everything after the first "Choose one" marker.
  const parts = desc.split(/\bChoose\s+one\b\s*:?/i);
  const after = String(parts.slice(1).join(' ') || '').trim();
  if (!after) return null;

  const normalized = after
    .replace(/\r\n/g, '\n')
    .replace(/\n+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  // Split into options. We use sentence boundaries as a heuristic.
  let rawOptions = normalized
    .split(/\s*\.[\s]+/)
    .map((s) => s.replace(/\.+$/g, '').trim())
    .filter(Boolean);

  if (rawOptions.length < 2 && /\bor\b/i.test(normalized)) {
    const orSplit = normalized
      .replace(/\.+$/g, '')
      .split(/\s+or\s+/i)
      .map((s) => s.trim())
      .filter(Boolean);
    if (orSplit.length >= 2) rawOptions = orSplit;
  }

  const options: ChooseOneOption[] = [];
  for (const optText of rawOptions) {
    const effs = inferEffectsSegment(optText, kind).filter((e) => (e as any).type !== 'discard') as any[];
    if (effs.length === 0) continue;

    // Build a compact label from the inferred effects (fallback to text).
    let label = optText;
    if (effs.length === 1) {
      const e: any = effs[0];
      if (e.type === 'healHP') label = `+${e.amount} HP`;
      else if (e.type === 'healSP') label = `+${e.amount} SP`;
      else if (e.type === 'gainMaxHpThisCombat') label = `+${e.amount} Max HP`;
      else if (e.type === 'block') label = `+${e.amount} Armor`;
      else if (e.type === 'damage') label = `Deal ${e.amount}`;
      else if (e.type === 'stun') label = `Stun ${e.turns}`;
      else if (e.type === 'reflect') label = `Reflect ${e.percent}%`;
      else if (e.type === 'nextMagicCardCostDoubleSP') label = `Next Magic x2 SP`;
      else if (e.type === 'weakenPlayerCards') label = `Weaken ${e.turns}t`;
    }

    if (label.length > 48) label = label.slice(0, 45).trim() + '…';

    options.push({ label, effects: effs as any });
  }

  if (options.length < 2) return null;
  return { type: 'chooseOne', options };
}

// Some cards include multiple delayed segments, e.g.:, e.g.:
// "Deal 20 damage. Deal 40 damage. Delay (1). Deal 80 damage. Delay (2)."
// Rule: a Delay (X) marker applies to the effect statement(s) immediately before it since the previous Delay marker.
// Any effects not followed by a delay marker are immediate.
function inferEffectsWithDelaySequence(
  title: string,
  subtitle: string,
  desc: string,
  kind: CardKind
): { effects: Effect[]; delayed: { turns: number; effects: Effect[] }[] } {
  const choose = extractChooseOne(desc, kind);
  if (choose) return { effects: [choose], delayed: [] };

  const prem = extractPremonitionChoice(title, desc);
  if (prem) return { effects: [prem], delayed: [] };

  if (String(title || '').trim().toLowerCase() === 'frenzy') return { effects: [{ type: 'frenzy' }], delayed: [] };

  // Hasten the Inevitable: match by text (so the mechanic still works if the backend title changes).
  {
    const low = String(`${subtitle}\n${desc}` || '').toLowerCase();
    if (low.includes('resolve all') && low.includes('delay') && low.includes('reduced in half')) {
      return { effects: [{ type: 'resolveAllDelayHalved' } as any], delayed: [] };
    }
  }

  // Variable X trade cards (require choosing X at play time).
  // - Blood Ritual: Lose X HP. Gain X SP.
  // - Forbidden Knowledge: Lose X SP. Gain X HP.
  const full = `${subtitle}\n${desc}`.replace(/\s+/g, ' ').trim();
  if (/\bLose\s+X\s+(?:Life|Health)\s+Points\b.*\bGain\s+X\s+Sanity\s+Points\b/i.test(full)) {
    return { effects: [{ type: 'xTrade', from: 'hp', to: 'sp' }], delayed: [] };
  }
  if (/\bLose\s+X\s+Sanity\s+Points\b.*\bGain\s+X\s+(?:Life|Health)\s+Points\b/i.test(full)) {
    return { effects: [{ type: 'xTrade', from: 'sp', to: 'hp' }], delayed: [] };
  }

  const source = `${subtitle}\n${desc}`.trim();

  // Paragraph-first parsing:
  // We want Delay markers to apply ONLY within a single effect block.
  // When descriptions come from HTML, toPlainText() converts <p> blocks to
  // "\n\n" so we can reliably split paragraphs here.
  //
  // Rule (matches your request):
  // - Everything inside a <p>...</p> is one effect block.
  // - Anything outside <p> becomes its own block.
  // - "Delay (X)" must appear at the END of the block to apply.
  const paragraphs = source.split(/\n{2,}/g).map((p) => p.trim()).filter(Boolean);
  if (paragraphs.length > 1) {
    const immediate: Effect[] = [];
    const delayed: { turns: number; effects: Effect[] }[] = [];

    const delayAtEndRe = /\bDelay\s*\(\s*([A-Za-z0-9-]+)\s*\)\s*\.?\s*$/i;

    const parseParaEffects = (p: string): Effect[] => {
      // Strip any leftover tags (should already be plain text, but keep it safe)
      const t = p.replace(/<\/?[^>]+>/g, '').trim();
      if (!t) return [];
      // Split into small sentence-ish chunks so existing matchers work well.
      const chunks = t
        .replace(/…/g, '.')
        .replace(/\.{2,}/g, '.')
        .split(/[.!?]+/g)
        .map((c) => c.trim())
        .filter(Boolean);
      const out: Effect[] = [];
      for (const c of chunks) {
        // True Strike: "Ignore enemy Armor." applies to the previous damage clause in the same block.
        if (/\bignore\s+(?:enemy\s+)?armor\b/i.test(c)) {
          for (let i = out.length - 1; i >= 0; i--) {
            const e: any = out[i];
            if (e?.type === 'damage') {
              out[i] = { type: 'damageIgnoreArmor', amount: Number(e.amount) || 0 } as any;
              break;
            }
          }
          continue;
        }
        out.push(...inferEffectsSegment(c, kind));
      }
      return out;
    };

    for (const rawPara of paragraphs) {
      const para = rawPara.replace(/<\/?[^>]+>/g, '').trim();
      if (!para) continue;


    // "You die next turn." is a special scheduled effect (not tied to a Delay marker).
      if (/\byou\s+die\s+next\s+turn\b/i.test(para)) {
        delayed.push({ turns: 1, effects: [{ type: 'die' }] });
        continue;
      }

      const dm = para.match(delayAtEndRe);
      if (dm) {
        const turns = parseCountToken(dm[1]);
        const body = para.replace(delayAtEndRe, '').trim();
        const effs = parseParaEffects(body);
        if (effs.length) {
          // Discard is a cost: never delay it.
          const costNow = effs.filter((e) => e.type === 'discard');
          const sched = effs.filter((e) => e.type !== 'discard');
          if (costNow.length) immediate.push(...costNow);
          if (turns && sched.length) delayed.push({ turns, effects: sched });
          else if (sched.length) immediate.push(...sched);
        }
        continue;
      }

      const paraEffects = parseParaEffects(para);
      if (/\bignore\s+(?:enemy\s+)?armor\b/i.test(para) && !paraEffects.some((e: any) => e?.type === 'damage' || e?.type === 'damageIgnoreArmor')) {
        for (let i = immediate.length - 1; i >= 0; i--) {
          const e: any = immediate[i];
          if (e?.type === 'damage') {
            immediate[i] = { type: 'damageIgnoreArmor', amount: Number(e.amount) || 0 } as any;
            break;
          }
        }
      }
      immediate.push(...paraEffects);
    }

    if (immediate.length === 0 && delayed.length === 0) {
      return { effects: inferEffects(title, subtitle, desc, kind), delayed: [] };
    }
    return { effects: immediate, delayed };
  }

  // Split by newlines and sentence boundaries without lookbehind (browser-safe).
  // IMPORTANT: descriptions are converted to plain text earlier (HTML <p>/<br> become "\n").
  // Preserve those paragraph breaks with a sentinel so Delay markers don't capture effects
  // from a previous paragraph.
  const PARA_BREAK = '__PARA_BREAK__';
  const normalized = source
    .replace(/\r\n/g, '\n')
    .replace(/\n+/g, `\n${PARA_BREAK}\n`)
    // Normalize ellipsis so we don't create empty clauses like "." "." "."
    .replace(/…/g, '.')
    .replace(/\.{2,}/g, '.')
    .replace(/([.!?])/g, '$1\n');

  const clauses = normalized
    .split(/\n+/)
    .map((c) => c.trim())
    .filter(Boolean);

  const immediate: Effect[] = [];
  const delayed: { turns: number; effects: Effect[] }[] = [];
  let buffer: Effect[] = [];

  const delayRe = /Delay\s*\(\s*([A-Za-z0-9-]+)\s*\)/i;

  // Previous implementation kept for reference (it could incorrectly group effects across <p> boundaries):
  /*
  for (const clause of clauses) {
    // True Strike: "Ignore enemy Armor" applies to the previous damage clause in the current block.
    if (/\bignore\s+(?:enemy\s+)?armor\b/i.test(clause)) {
      for (let i = buffer.length - 1; i >= 0; i--) {
        const e: any = buffer[i];
        if (e?.type === 'damage') {
          buffer[i] = { type: 'damageIgnoreArmor', amount: Number(e.amount) || 0 } as any;
          break;
        }
      }
      continue;
    }

    // "You die next turn." is a special scheduled effect (not tied to a Delay marker).
    if (/\byou\s+die\s+next\s+turn\b/i.test(clause)) {
      delayed.push({ turns: 1, effects: [{ type: 'die' }] });
      continue;
    }

    const dm = clause.match(delayRe);
    if (dm) {
      const turns = parseCountToken(dm[1]);
      if (turns && buffer.length > 0) {
        // Discard is a cost: never delay it. If it was in the buffer, keep it immediate.
        const costNow = buffer.filter((e) => e.type === 'discard');
        const sched = buffer.filter((e) => e.type !== 'discard');
        if (costNow.length) immediate.push(...costNow);
        if (sched.length) delayed.push({ turns, effects: sched });
      }
      buffer = [];
      continue;
    }

    buffer.push(...inferEffectsSegment(clause, kind));
  }
  */

  for (const rawClause of clauses) {
    // If we hit a paragraph boundary marker, commit anything buffered so far as immediate.
    if (rawClause === PARA_BREAK) {
      if (buffer.length) {
        immediate.push(...buffer);
        buffer = [];
      }
      continue;
    }

    // Backward-compatible: if raw HTML slipped through, treat <p> as a boundary too.
    if (/^\s*<\s*p\b/i.test(rawClause) && buffer.length) {
      immediate.push(...buffer);
      buffer = [];
    }

    // Strip HTML tags for matching/parsing (mechanics are inferred from text).
    const clause = rawClause.replace(/<\/?[^>]+>/g, '').trim();
    if (!clause) continue;

    // True Strike: "Ignore enemy Armor" applies to the previous damage clause in the current block.
    if (/\bignore\s+(?:enemy\s+)?armor\b/i.test(clause)) {
      for (let i = buffer.length - 1; i >= 0; i--) {
        const e: any = buffer[i];
        if (e?.type === 'damage') {
          buffer[i] = { type: 'damageIgnoreArmor', amount: Number(e.amount) || 0 } as any;
          break;
        }
      }
      continue;
    }

    // "You die next turn." is a special scheduled effect (not tied to a Delay marker).
    if (/\byou\s+die\s+next\s+turn\b/i.test(clause)) {
      delayed.push({ turns: 1, effects: [{ type: 'die' }] });
      continue;
    }

    const dm = clause.match(delayRe);
    if (dm) {
      const turns = parseCountToken(dm[1]);
      if (turns && buffer.length > 0) {
        // Discard is a cost: never delay it. If it was in the buffer, keep it immediate.
        const costNow = buffer.filter((e) => e.type === 'discard');
        const sched = buffer.filter((e) => e.type !== 'discard');
        if (costNow.length) immediate.push(...costNow);
        if (sched.length) delayed.push({ turns, effects: sched });
      }
      buffer = [];
      continue;
    }

    buffer.push(...inferEffectsSegment(clause, kind));
  }

  if (buffer.length) immediate.push(...buffer);

  if (immediate.length === 0 && delayed.length === 0) {
    return { effects: inferEffects(title, subtitle, desc, kind), delayed: [] };
  }

  return { effects: immediate, delayed };
}

// Premonition: "Draw a card from one of your decks. Reduce its cost to 0. Chain."
// We model this as a choice: draw from Physical or draw from Magic, with the drawn
// card instance cost reduced to 0.
function extractPremonitionChoice(title: string, desc: string): null | { type: 'chooseOne'; options: ChooseOneOption[] } {
  if (String(title || '').trim().toLowerCase() !== 'premonition') return null;
  // Require the core phrasing to avoid accidental triggering.
  if (!/reduce\s+its\s+cost\s+to\s+0/i.test(desc)) return null;
  if (!/draw\s+a\s+card/i.test(desc)) return null;

  const options: ChooseOneOption[] = [
    { label: 'Draw Physical (0)', effects: [{ type: 'drawFromDeck', kind: 'physical', amount: 1, setCostTo0: true }] },
    { label: 'Draw Magic (0)', effects: [{ type: 'drawFromDeck', kind: 'magic', amount: 1, setCostTo0: true }] },
  ];
  return { type: 'chooseOne', options };
}

// Infer effects from a single text segment/line, WITHOUT applying defaults.
// Used by the Delay-sequence parser so empty lines don't create fake damage.
function inferEffectsSegment(text: string, kind: CardKind): Effect[] {
  const effects: Effect[] = [];

  // Behold the Path: "The next card you play has cost 0."
  if (/\bnext\s+card\s+you\s+play\s+has\s+cost\s+0\b/i.test(text) || /\bnext\s+card\s+you\s+play\s+costs\s+0\b/i.test(text)) {
    effects.push({ type: 'nextCardCost0' });
  }

  // Hexbinder debuff: player's next Magic card costs double SP.
  // Matches phrases like:
  // - "The player's next magic card costs double SP."
  // - "Player's next magic card costs double SP."
  // - "Next magic card costs double SP."
  if (
    /\bnext\s+magic\s+card\s+costs?\s+(?:double|twice)\s+sp\b/i.test(text) ||
    /\bplayer'?s?\s+next\s+magic\s+card\s+costs?\s+(?:double|twice)\s+sp\b/i.test(text) ||
    /\bthe\s+player'?s?\s+next\s+magic\s+card\s+costs?\s+(?:double|twice)\s+sp\b/i.test(text)
  ) {
    effects.push({ type: 'nextMagicCardCostDoubleSP' });
  }

  // Hexbinder debuff: weaken the player's cards for N turns.
  // Matches:
  // - "Player's cards are weakened during the next two turns."
  // - "Player's cards are weakened for 2 turns."
  // Supports number-words via matchCount (two, three, etc).
  const weakenTurns =
    matchCount(text, /cards?\s+are\s+weakened\s+during\s+the\s+next\s+([A-Za-z0-9-]+)\s+turns?/i) ??
    matchCount(text, /cards?\s+are\s+weakened\s+for\s+([A-Za-z0-9-]+)\s+turns?/i);

  if (weakenTurns) {
    effects.push({ type: 'weakenPlayerCards', turns: Math.max(1, weakenTurns) });
  }

  

// Player debuff on enemy: reduce enemy outgoing damage for N enemy turns.
// Example: "Enemy deals 50% less damage for the next 2 turns."
const enemyLessPct =
  matchNumber(text, /Enemy\s+deals\s+(\d+)\s*%\s+less\s+damage\s+for\s+the\s+next\s+([A-Za-z0-9-]+)\s+turns?/i)
  ?? matchNumber(text, /Enemy\s+deals\s+(\d+)\s*%\s+less\s+damage\s+for\s+([A-Za-z0-9-]+)\s+turns?/i);
const enemyLessTurns =
  matchCount(text, /Enemy\s+deals\s+\d+\s*%\s+less\s+damage\s+for\s+the\s+next\s+([A-Za-z0-9-]+)\s+turns?/i)
  ?? matchCount(text, /Enemy\s+deals\s+\d+\s*%\s+less\s+damage\s+for\s+([A-Za-z0-9-]+)\s+turns?/i);

if (enemyLessPct != null && enemyLessTurns) {
  const pct = Math.max(0, Math.min(100, Math.floor(Number(enemyLessPct) || 0)));
  const mult = Math.max(0, Math.min(1, 1 - pct / 100));
  effects.push({ type: 'weakenEnemyDamage', mult, turns: Math.max(1, enemyLessTurns) } as any);
}

// Channel: turn-based damage multipliers
  if (/\bdouble\s+the\s+damage\s+you\s+deal\s+this\s+turn\b/i.test(text)) {
    effects.push({ type: 'doubleDamageDealtThisTurn' });
  }
  if (/\bdouble\s+the\s+damage\s+you\s+deal\s+next\s+(?:player\s+)?turn\b/i.test(text)) {
    effects.push({ type: 'doubleDamageDealtNextTurn' });
  }
  if (/\bdouble\s+the\s+damage\s+you\s+take\s+next\s+(?:enemy\s+)?turn\b/i.test(text)) {
    effects.push({ type: 'doubleDamageTakenNextTurn' });
  }

  // Focused Blast: armor-scaling damage
  if (/\bdeal\s+damage\s+equal\s+to\s+your\s+armor\s+points\b/i.test(text) || /\bdeal\s+damage\s+equal\s+to\s+your\s+armor\b/i.test(text)) {
    effects.push({ type: 'damageEqualToArmor' });
  }
  if (
    /\blose\s+all\s+your\s+armor\s+points\b/i.test(text)
    || /\blose\s+all\s+your\s+armor\b/i.test(text)
    || /\blose\s+all\s+armor\s+points\b/i.test(text)
    || /\blose\s+all\s+armor\b/i.test(text)
    || /\bremove\s+all\s+(?:your\s+)?armor\s+points\b/i.test(text)
    || /\bremove\s+all\s+(?:your\s+)?armor\b/i.test(text)
  ) {
    effects.push({ type: 'setArmor', value: 0 });
  }

  // Shatter and similar: destroy all ENEMY armor.
  if (/\b(?:destroy|remove)\s+all\s+(?:enemy|opponent'?s?)\s+armor(?:\s+points)?\b/i.test(text)) {
    effects.push({ type: 'setOpponentArmor', value: 0 });
  }

  // Damage
  const dmg = matchNumber(text, /Deal\s+(\d+)\s+damage/i) ?? matchNumber(text, /(\d+)\s+damage/i);
  if (dmg) effects.push({ type: 'damage', amount: dmg });

  // Set Life/Health and Sanity to a value (e.g. "Set your Life and Sanity Points to 100")
  const setBoth = matchNumber(text, /Set\s+your\s+(?:Life|Health)\s+and\s+Sanity\s+Points\s+to\s+(\d+)/i);
  if (setBoth !== null) {
    effects.push({ type: 'setHP', value: setBoth });
    effects.push({ type: 'setSP', value: setBoth });
  }

  // Draw one from each deck
  if (/Draw\s+a\s+card\s+from\s+each\s+deck/i.test(text)) {
    effects.push({ type: 'drawFromDeck', kind: 'physical', amount: 1 });
    effects.push({ type: 'drawFromDeck', kind: 'magic', amount: 1 });
  }


  // Next-attack bonus (e.g. "If the next card you play this turn is an attack, it deals 10 extra damage.")
  if (/\bnext\s+card\s+you\s+play\s+this\s+turn\s+is\s+an\s+attack\b/i.test(text)) {
    const bonus = matchNumber(text, /deals\s+(\d+)\s+extra\s+damage/i) ?? matchNumber(text, /(\d+)\s+extra\s+damage/i);
    if (bonus) effects.push({ type: 'nextAttackBonus', amount: bonus });
  }

  // True Strike (discard trigger): "your next attack ignores Armor".
  if (/\byour\s+next\s+attack\s+ignores?\s+armor\b/i.test(text) || /\bnext\s+attack\s+ignores?\s+armor\b/i.test(text)) {
    effects.push({ type: 'nextAttackIgnoreArmor', count: 1 } as any);
  }

  const reduceUtilityCostThisTurn =
    matchNumber(text, /This\s+turn\s*,?\s*all\s+utility\s+cards?\s+costs?\s+are\s+reduced\s+by\s+(\d+)\b/i)
    ?? matchNumber(text, /This\s+turn\s*,?\s*utility\s+cards?\s+costs?\s+are\s+reduced\s+by\s+(\d+)\b/i)
    ?? matchNumber(text, /Reduce\s+all\s+utility\s+cards?\s+costs?\s+by\s+(\d+)\s+this\s+turn\b/i);
  if (reduceUtilityCostThisTurn) {
    effects.push({ type: 'reduceUtilityCardCostsThisTurn', amount: reduceUtilityCostThisTurn } as any);
  }

  // Pull the Thread: "Reduce all Delay stacks by N".
  const redDelay =
    matchCount(text, /reduce\s+all\s+delay\s+stacks?\s+by\s+([A-Za-z0-9-]+)/i) ??
    matchCount(text, /reduce\s+all\s+delay\s+by\s+([A-Za-z0-9-]+)/i);
  if (redDelay) {
    effects.push({ type: 'reduceAllDelay', amount: Math.max(1, redDelay) } as any);
  }

  // Double armor / HP
  if (/Double\s+your\s+Armor\s+Points/i.test(text)) {
    effects.push({ type: 'doubleArmor' });
  }
  if (/Double\s+your\s+(?:Life|Health)\s+Points/i.test(text)) {
    effects.push({ type: 'doubleHP' });
  }
  if (/Double\s+your\s+(?:Sanity(?:\s+Points)?|SP)\b/i.test(text)) {
    effects.push({ type: 'doubleSP' });
  }

  // Tutor / deck search
  if (/Search\s+your\s+magic\s+deck\s+for\s+a\s+card/i.test(text)) {
    effects.push({ type: 'tutor', kind: 'magic' });
  }
  if (/Search\s+your\s+physical\s+deck\s+for\s+a\s+card/i.test(text)) {
    effects.push({ type: 'tutor', kind: 'physical' });
  }
  // Tutor from the combined deck (physical + magic)
  // Example: "Look through your deck and choose a card. Put that card in your hand."
  if (/Look\s+through\s+your\s+deck\s+and\s+choose\s+a\s+card/i.test(text)) {
    effects.push({ type: 'tutor', kind: 'any' });
  }
  if (/Look\s+through\s+your\s+discard\s+pile\s+and\s+choose\s+a\s+card/i.test(text)) {
    effects.push({ type: 'tutor', kind: 'any', zone: 'discard' } as any);
  }

  
// Some cards explicitly apply Armor gain twice (e.g. "Gain X Armor twice.").
// We model this as two separate block effects so relic modifiers and clamping apply consistently.
const blockTwice = matchNumber(text, /Gain\s+(\d+)\s+Armor\s+twice\b/i);
const handledBlockTwice = blockTwice != null && Number.isFinite(blockTwice);
if (handledBlockTwice) {
  const amt = Math.max(0, Math.floor(Number(blockTwice) || 0));
  effects.push({ type: 'block', amount: amt } as any);
  effects.push({ type: 'block', amount: amt } as any);
}

// Block/Armor gain
  // Special: "Gain 10 Armor for each card you played this turn"
  const blockPerPlays = matchNumber(text, /Gain\s+(\d+)\s+Armor\s+for\s+each\s+card\s+you\s+played\s+this\s+turn/i)
    ?? matchNumber(text, /Gain\s+(\d+)\s+Armor\s+for\s+each\s+card\s+played\s+this\s+turn/i);
  if (!handledBlockTwice && blockPerPlays) effects.push({ type: 'blockPerPlaysThisTurn', amountPer: blockPerPlays } as any);


  // Heal per cards played this turn (e.g. "Gain 10 HP for each card played this turn")
  const healPerPlays = matchNumber(text, /Gain\s+(\d+)\s+HP\s+for\s+each\s+card\s+you\s+played\s+this\s+turn/i)
    ?? matchNumber(text, /Gain\s+(\d+)\s+HP\s+for\s+each\s+card\s+played\s+this\s+turn/i)
    ?? matchNumber(text, /Gain\s+(\d+)\s+Health\s+Points\s+for\s+each\s+card\s+you\s+played\s+this\s+turn/i)
    ?? matchNumber(text, /Gain\s+(\d+)\s+Health\s+Points\s+for\s+each\s+card\s+played\s+this\s+turn/i);
  if (healPerPlays) effects.push({ type: 'healHPPerPlaysThisTurn', amountPer: healPerPlays });

  // Gain SP per cards played this turn (e.g. "Gain 10 SP for each card you played this turn")
  const spPerPlays = matchNumber(text, /Gain\s+(\d+)\s+SP\s+for\s+each\s+card\s+you\s+played\s+this\s+turn/i)
    ?? matchNumber(text, /Gain\s+(\d+)\s+SP\s+for\s+each\s+cards?\s+played\s+this\s+turn/i)
    ?? matchNumber(text, /Gain\s+(\d+)\s+Sanity\s+Points\s+for\s+each\s+card\s+you\s+played\s+this\s+turn/i)
    ?? matchNumber(text, /Gain\s+(\d+)\s+Sanity\s+Points\s+for\s+each\s+cards?\s+played\s+this\s+turn/i);
  if (spPerPlays) effects.push({ type: 'healSPPerPlaysThisTurn', amountPer: spPerPlays });

  // Only treat as a flat armor gain when it's not the per-play wording.
  if (!handledBlockTwice && !blockPerPlays) {
    const block = matchNumber(text, /Gain\s+(\d+)\s+Armor/i) ?? matchNumber(text, /(\d+)\s+Armor/i);
    if (block) effects.push({ type: 'block', amount: block });
  }

  // Healing
const healHP = matchNumber(text, /Gain\s+(\d+)\s+(?:Health\s+Points|HP)\b/i);
  if (healHP) effects.push({ type: 'healHP', amount: healHP });
  const healSP =
  matchNumber(text, /Gain\s+(\d+)\s+(?:Sanity\s+Points|SP)\b/i)
  ?? matchNumber(text, /Gain\s+(\d+)\s+Sanity\b/i);
if (healSP) effects.push({ type: 'healSP', amount: healSP });

  // Temporary Max HP increase (Endure and similar).
  const gainMaxHp =
    matchNumber(text, /Gain\s+(\d+)\s+maximum\s+(?:Health\s+Points|HP)\b/i) ??
    matchNumber(text, /Increase\s+(?:your\s+)?maximum\s+(?:Health\s+Points|HP)\s+by\s+(\d+)\b/i);
  if (gainMaxHp) effects.push({ type: 'gainMaxHpThisCombat', amount: gainMaxHp });

  // Grim Renewal and similar: "This turn, gain 1 HP for every point of damage you deal."
  // (Heal scales with damage dealt after armor absorption, capped at target's current HP+Armor; no overkill.)
  const healPerDamageThisTurn =
    matchCount(text, /This\s+turn\s*,\s*gain\s+([A-Za-z0-9-]+)\s+(?:HP|Health\s+Points)\s+for\s+every\s+point\s+of\s+damage\s+you\s+deal\b/i) ??
    matchCount(text, /This\s+turn\s*,\s*gain\s+([A-Za-z0-9-]+)\s+(?:HP|Health\s+Points)\s+for\s+each\s+point\s+of\s+damage\s+you\s+deal\b/i);
  if (healPerDamageThisTurn) effects.push({ type: 'healOnDamageDealtThisTurn', amountPerDamage: healPerDamageThisTurn });

  // Losing resources (non-cost effects)
const loseHP =
  matchNumber(text, /Lose\s+(\d+)\s+(?:Health\s+Points|HP)\b/i)
  ?? matchNumber(text, /Recei?ve\s+(\d+)\s+damage\b/i);
  if (loseHP) effects.push({ type: 'loseHP', amount: loseHP });
  const loseSP =
  matchNumber(text, /Lose\s+(\d+)\s+(?:Sanity\s+Points|SP)\b/i)
  ?? matchNumber(text, /Lose\s+(\d+)\s+Sanity\b/i)
  ?? matchNumber(text, /Burn\s+(\d+)\s+(?:Sanity\s+Points|SP)\b/i)
  ?? matchNumber(text, /Burn\s+(\d+)\s+Sanity\b/i);
if (loseSP) effects.push({ type: 'loseSP', amount: loseSP });

  // Draw random variants (optional count)
  const randomPhysical = matchCountOptional(text, /Draw\s+(?:([A-Za-z0-9-]+)\s+)?random\s+physical\s+cards?/i);
  if (randomPhysical) effects.push({ type: 'drawRandomPhysical', amount: randomPhysical });

  const randomMagic = matchCountOptional(text, /Draw\s+(?:([A-Za-z0-9-]+)\s+)?random\s+magic\s+cards?/i);
  if (randomMagic) effects.push({ type: 'drawRandomMagic', amount: randomMagic });

  const randomAny = matchCountOptional(text, /Draw\s+(?:([A-Za-z0-9-]+)\s+)?random\s+cards?/i);
  if (randomAny) effects.push({ type: 'drawRandom', amount: randomAny });

  // Standard draw (avoid double-counting random)
  if (!/\brandom\b/i.test(text)) {
    const draw =
      matchCount(text, /Draw\s+([A-Za-z0-9-]+)\s+additional\s+cards?/i) ??
      matchCount(text, /Draw\s+(a|an)\s+additional\s+card\b/i) ??
      matchCount(text, /Draw\s+([A-Za-z0-9-]+)\s+more\s+cards?/i) ??
      matchCount(text, /Draw\s+([A-Za-z0-9-]+)\s+cards?/i) ??
      matchCount(text, /Draw\s+([A-Za-z0-9-]+)\s+card\b/i) ??
      matchCount(text, /Draw\s+(a|an)\s+card\b/i) ??
      matchCount(text, /Draw\s+([A-Za-z0-9-]+)\s*[.!?]/i);
    if (draw) effects.push({ type: 'draw', amount: draw });
  }

  // Reflect
  const reflect =
    matchNumber(text, /Reflect\s+(\d+)\s*%\s+of\s+the\s+next\s+damage/i) ??
    matchNumber(text, /Reflect\s+(\d+)\s*%\s+of\s+next\s+damage/i) ??
    matchNumber(text, /Reflect\s+(\d+)\s*%/i);
  if (reflect !== null) {
    effects.push({ type: 'reflect', percent: Math.max(0, Math.min(500, reflect)) });
  }

  // Echo of Ruin and similar: "Next time you reflect damage, gain that much SP."
  if (/\bnext\s+time\s+you\s+reflect\s+damage\s*(?:,|:)\s*gain\s+that\s+much\s+(?:sanity\s+points|sp)\b/i.test(text)) {
    effects.push({ type: 'nextReflectGainSp' });
  }

  // The Mirror Remembers and similar: "The next time you reflect damage, trigger it twice."
  if (/\bnext\s+time\s+you\s+reflect\s+damage\b[\s\S]{0,80}\btrigger\s+it\s+twice\b/i.test(text)) {
    effects.push({ type: 'nextReflectTriggerTwice' });
  }

  // Negate next incoming damage
  if (/Reduce\s+the\s+next\s+damage\s+you\s+would\s+(?:receive|take)\s+to\s+(?:0|zero)\b/i.test(text)) {
    effects.push({ type: 'negateNextDamage' });
  }

  // Stun (X)
  const stunTurns =
    matchCount(text, /Stun\s*\(\s*([A-Za-z0-9-]+)\s*\)/i) ??
    matchCount(text, /Stun\s+(?:(?:the\s+)?(?:enemy|opponent|target|player)\s+)?(?:for\s+)?([A-Za-z0-9-]+)\s+turns?/i) ??
    matchCount(text, /Stun\s+(?:for\s+)?([A-Za-z0-9-]+)\s+turns?/i);
  if (stunTurns) {
    effects.push({ type: 'stun', turns: Math.max(1, stunTurns) });
  } else if (shouldApplyImplicitStun(text)) {
    effects.push({ type: 'stun', turns: 1 });
  }

  // Discard
  const discard = matchCount(text, /Discard\s+([A-Za-z0-9-]+)\s+cards?/i);
  if (discard) effects.push({ type: 'discard', amount: discard });
// Third Eye-style "pick one from the top N":
// "Look at the top 3 cards in your deck, put one in your hand and the rest in the discard pile."
const pickOneTopAny = matchCount(
  text,
  /Look\s+at\s+the\s+top\s+([A-Za-z0-9-]+)\s+cards?\s+in\s+your\s+deck\b[\s\S]{0,120}\bput\s+one\s+in\s+your\s+hand\b[\s\S]{0,120}\brest\s+in\s+(?:the\s+)?discard/i
);
if (pickOneTopAny) {
  // "your deck" means the player's combined combat deck, not the source card's own kind.
  // Keep explicit physical/magic wording separate; generic wording should inspect both sides.
  effects.push({ type: 'peekPickOneDiscardRestAny', count: pickOneTopAny } as any);
}





  // Peek/Reorder (Third Eye): "Look at the top N cards in your physical/magic deck. Arrange them in any order and return them."
  const peekPhys = matchCount(text, /Look\s+at\s+the\s+top\s+([A-Za-z0-9-]+)\s+cards\s+in\s+your\s+physical\s+deck/i);
  if (peekPhys) effects.push({ type: 'peekReorderPhysical', count: peekPhys });

  const peekMagic = matchCount(text, /Look\s+at\s+the\s+top\s+([A-Za-z0-9-]+)\s+cards\s+in\s+your\s+magic\s+deck/i);
  if (peekMagic) effects.push({ type: 'peekReorderMagic', count: peekMagic });

  // Generic peek/reorder: "Look at the top N cards in your deck" (deck chosen by card kind)
  const peekAny = matchCount(text, /Look\s+at\s+the\s+top\s+([A-Za-z0-9-]+)\s+cards?\s+in\s+your\s+deck\b[^\n.]*\breorder\b/i);
  if (peekAny) {
    effects.push({ type: 'peekReorderDeck', count: peekAny } as any);
  }

  return effects;
}

function inferEffects(title: string, subtitle: string, desc: string, kind: CardKind): Effect[] {
  const text = `${title}\n${subtitle}\n${desc}`;

  const effects: Effect[] = [];

  // Damage
  const dmg = matchNumber(text, /\bDe(?:al|als)\s+(\d+)\s+(?:damage|dmg)\b/i);
  if (dmg) effects.push({ type: 'damage', amount: dmg });

  const reduceUtilityCostThisTurn =
    matchNumber(text, /This\s+turn\s*,?\s*all\s+utility\s+cards?\s+costs?\s+are\s+reduced\s+by\s+(\d+)\b/i)
    ?? matchNumber(text, /This\s+turn\s*,?\s*utility\s+cards?\s+costs?\s+are\s+reduced\s+by\s+(\d+)\b/i)
    ?? matchNumber(text, /Reduce\s+all\s+utility\s+cards?\s+costs?\s+by\s+(\d+)\s+this\s+turn\b/i);
  if (reduceUtilityCostThisTurn) effects.push({ type: 'reduceUtilityCardCostsThisTurn', amount: reduceUtilityCostThisTurn } as any);

  // Block/Armor gain
  // Special: "Gain 10 Armor for each card you played this turn"
  const blockPerPlays = matchNumber(text, /Gain\s+(\d+)\s+Armor\s+for\s+each\s+card\s+you\s+played\s+this\s+turn/i)
    ?? matchNumber(text, /Gain\s+(\d+)\s+Armor\s+for\s+each\s+card\s+played\s+this\s+turn/i);
  if (blockPerPlays) effects.push({ type: 'blockPerPlaysThisTurn', amountPer: blockPerPlays });

  // Only treat as a flat armor gain when it's not the per-play wording.
  if (!blockPerPlays) {
    const block = matchNumber(text, /Gain\s+(\d+)\s+Armor/i) ?? matchNumber(text, /(\d+)\s+Armor/i);
    if (block) effects.push({ type: 'block', amount: block });
  }

  // Healing
const healHP = matchNumber(text, /Gain\s+(\d+)\s+(?:Health\s+Points|HP)\b/i);
  if (healHP) effects.push({ type: 'healHP', amount: healHP });
  const healSP =
  matchNumber(text, /Gain\s+(\d+)\s+(?:Sanity\s+Points|SP)\b/i)
  ?? matchNumber(text, /Gain\s+(\d+)\s+Sanity\b/i);
if (healSP) effects.push({ type: 'healSP', amount: healSP });

  // Temporary Max HP increase (Endure and similar).
  const gainMaxHp =
    matchNumber(text, /Gain\s+(\d+)\s+maximum\s+(?:Health\s+Points|HP)\b/i) ??
    matchNumber(text, /Increase\s+(?:your\s+)?maximum\s+(?:Health\s+Points|HP)\s+by\s+(\d+)\b/i);
  if (gainMaxHp) effects.push({ type: 'gainMaxHpThisCombat', amount: gainMaxHp });

  // Grim Renewal and similar: "This turn, gain 1 HP for every point of damage you deal."
  const healPerDamageThisTurn =
    matchCount(text, /This\s+turn\s*,\s*gain\s+([A-Za-z0-9-]+)\s+(?:HP|Health\s+Points)\s+for\s+every\s+point\s+of\s+damage\s+you\s+deal\b/i) ??
    matchCount(text, /This\s+turn\s*,\s*gain\s+([A-Za-z0-9-]+)\s+(?:HP|Health\s+Points)\s+for\s+each\s+point\s+of\s+damage\s+you\s+deal\b/i);
  if (healPerDamageThisTurn) effects.push({ type: 'healOnDamageDealtThisTurn', amountPerDamage: healPerDamageThisTurn });

  // Losing resources (non-cost effects)
const loseHP =
  matchNumber(text, /Lose\s+(\d+)\s+(?:Health\s+Points|HP)\b/i)
  ?? matchNumber(text, /Recei?ve\s+(\d+)\s+damage\b/i);
  if (loseHP) effects.push({ type: 'loseHP', amount: loseHP });
  const loseSP =
  matchNumber(text, /Lose\s+(\d+)\s+(?:Sanity\s+Points|SP)\b/i)
  ?? matchNumber(text, /Lose\s+(\d+)\s+Sanity\b/i)
  ?? matchNumber(text, /Burn\s+(\d+)\s+(?:Sanity\s+Points|SP)\b/i)
  ?? matchNumber(text, /Burn\s+(\d+)\s+Sanity\b/i);
if (loseSP) effects.push({ type: 'loseSP', amount: loseSP });

  // Draw (random variants)
  // - "Draw a random card"
  // - "Draw a random physical card"
  // - "Draw a random magic card"
  // Supports an optional count token (digits or number-words) before "random".
  // Example: "Draw two random cards".
  const randomPhysical = matchCountOptional(text, /Draw\s+(?:([A-Za-z0-9-]+)\s+)?random\s+physical\s+cards?/i);
  if (randomPhysical) effects.push({ type: 'drawRandomPhysical', amount: randomPhysical });

  const randomMagic = matchCountOptional(text, /Draw\s+(?:([A-Za-z0-9-]+)\s+)?random\s+magic\s+cards?/i);
  if (randomMagic) effects.push({ type: 'drawRandomMagic', amount: randomMagic });

  const randomAny = matchCountOptional(text, /Draw\s+(?:([A-Za-z0-9-]+)\s+)?random\s+cards?/i);
  if (randomAny) effects.push({ type: 'drawRandom', amount: randomAny });

  // Draw (extra cards). Supports digits and number-words: "Draw 2 cards", "Draw two cards", "Draw a card".
  // IMPORTANT: actual draw timing/which deck is handled in the engine.
  // If the description contains "random", we assume it refers to random draw variants above.
  // (Prevents "Draw a random card" from being misinterpreted as a normal draw.)
  if (!/\brandom\b/i.test(text)) {
    const draw =
      matchCount(text, /Draw\s+([A-Za-z0-9-]+)\s+additional\s+cards?/i) ??
      matchCount(text, /Draw\s+(a|an)\s+additional\s+card\b/i) ??
      matchCount(text, /Draw\s+([A-Za-z0-9-]+)\s+more\s+cards?/i) ??
      matchCount(text, /Draw\s+([A-Za-z0-9-]+)\s+cards?/i) ??
      matchCount(text, /Draw\s+([A-Za-z0-9-]+)\s+card\b/i) ??
      matchCount(text, /Draw\s+(a|an)\s+card\b/i) ??
      // Tolerate "Draw two." / "Draw 2." (no 'card' word)
      matchCount(text, /Draw\s+([A-Za-z0-9-]+)\s*[.!?]/i);
    if (draw) effects.push({ type: 'draw', amount: draw });
  }

  // Reflect shield (next hit)
  const reflect =
    matchNumber(text, /Reflect\s+(\d+)\s*%\s+of\s+the\s+next\s+damage/i) ??
    matchNumber(text, /Reflect\s+(\d+)\s*%\s+of\s+next\s+damage/i) ??
    matchNumber(text, /Reflect\s+(\d+)\s*%/i);
  if (reflect !== null) {
    effects.push({ type: 'reflect', percent: Math.max(0, Math.min(500, reflect)) });
  }

  // Negate next incoming damage
  if (/Reduce\s+the\s+next\s+damage\s+you\s+would\s+(?:receive|take)\s+to\s+(?:0|zero)\b/i.test(text)) {
    effects.push({ type: 'negateNextDamage' });
  }

  // Stun (X): supports digits and number-words inside parentheses.
  // Examples: "Stun (2)", "Stun(two)", "Stun the enemy for 2 turns", and a fallback "Stun" = 1.
  const stunTurns =
    matchCount(text, /Stun\s*\(\s*([A-Za-z0-9-]+)\s*\)/i) ??
    matchCount(text, /Stun\s+(?:(?:the\s+)?(?:enemy|opponent|target|player)\s+)?(?:for\s+)?([A-Za-z0-9-]+)\s+turns?/i) ??
    // Tolerate "Stun for two turns" / "Stun 2 turns"
    matchCount(text, /Stun\s+(?:for\s+)?([A-Za-z0-9-]+)\s+turns?/i);
  if (stunTurns) {
    effects.push({ type: 'stun', turns: Math.max(1, stunTurns) });
  } else if (shouldApplyImplicitStun(text)) {
    effects.push({ type: 'stun', turns: 1 });
  }

  // Discard (player chooses cards to discard)
  const discard = matchCount(text, /Discard\s+([A-Za-z0-9-]+)\s+cards?/i);
  if (discard) effects.push({ type: 'discard', amount: discard });

  return effects;
}

function matchNumber(text: string, re: RegExp): number | null {
  const m = text.match(re);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function shouldApplyImplicitStun(text: string): boolean {
  if (!/stun/i.test(text)) return false;
  // Do not infer a stun application from conditional/reference wording like
  // "If the enemy has Stun, ..." or "while the opponent has Stun ...".
  if (/(?:if|when|while)\s+(?:the\s+)?(?:enemy|opponent|target|player)\s+has\s+stun/i.test(text)) {
    return false;
  }
  return true;
}

function matchCount(text: string, re: RegExp): number | null {
  const m = text.match(re);
  if (!m) return null;
  return parseCountToken(m[1]);
}

// Like matchCount, but supports patterns where the capture group is optional.
// If the regex matches but the group is missing/empty, returns 1.
function matchCountOptional(text: string, re: RegExp): number | null {
  const m = text.match(re);
  if (!m) return null;
  const token = m[1];
  if (token == null || String(token).trim() === '') return 1;
  return parseCountToken(token);
}

function parseCountToken(token: string): number | null {
  const t = String(token || '').trim().toLowerCase();
  if (!t) return null;

  // Numeric forms
  if (/^\d+$/.test(t)) {
    const n = parseInt(t, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  const map: Record<string, number> = {
    a: 1,
    an: 1,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12,
    thirteen: 13,
    fourteen: 14,
    fifteen: 15,
    sixteen: 16,
    seventeen: 17,
    eighteen: 18,
    nineteen: 19,
    twenty: 20,
  };

  // Hyphenated forms like 'twenty-one'
  if (t.includes('-')) {
    const parts = t.split('-').filter(Boolean);
    if (parts.length === 2 && parts[0] in map && parts[1] in map) {
      const n = (map[parts[0]] ?? 0) + (map[parts[1]] ?? 0);
      return n > 0 ? n : null;
    }
  }

  const n = map[t];
  return Number.isFinite(n) && n > 0 ? n : null;
}
